// Report Templates Module
import { apiCall, createReportTemplate as createReportTemplateAPI, updateReportTemplate, createTemplateElementsBulk, getReportTemplates, getTemplateElements, deleteReportTemplate } from '../core/api.js';
import { auth } from '../core/auth.js';
import { showError, showSuccess, showConfirmDialog } from '../core/ui.js';
import { t } from '../core/i18n.js';
import { updatePageTitle, updateActiveNavigation } from './navigation.js';
import { getCurrentUser } from '../utils/helpers.js';
import { showStandardTexts } from './standard-texts.js';
import { showTags } from './tags.js';

// ============================================================================
// Utility Functions
// ============================================================================

/**
 * Escape HTML to prevent XSS
 */
function escapeHtml(text) {
    if (!text) return '';
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
}

// ============================================================================
// State Management
// ============================================================================

const reportTemplatesState = {
    templates: [],
    selectedTemplateId: null,
    templateName: '',
    templateDescription: '',
    isReleased: false,
    reportType: null, // 'data_collection', 'filter', 'statistics'
    documentationLayout: null, // 'structured', 'dynamic' (only for data_collection)
    searchQuery: '',
    currentPage: 'frontpage', // frontpage, results, data, conclusion
    elements: [], // Array of template elements with position, page, zone, type, data
    hasUnsavedChanges: false // Track if there are unsaved configuration changes
};

// Fixed position mapping for each zone
const zonePositions = {
    // Front Page (positions 1-10)
    'header-left': 1,
    'header-right': 2,
    'main-content': 3,
    'headline-left': 4,
    'headline-right': 5,
    'detail-1': 6,
    'detail-2': 7,
    'detail-3': 8,
    'detail-4': 9,
    'footer-left': 10,
    'footer-right': 11,
    
        // Results Page (positions 12-16)
        'results-top': 12,
        'results-upper': 13,
        'results-middle': 14,
        'results-lower': 15,
        'results-bottom': 16,    // Documentation Page (position 21)
    'data-collection': 21,
    
    // Final Remarks Page (positions 31-40)
    'conclusion-content': 31
};

/**
 * Display the Report Templates page with three-column layout
 */
export async function showReportTemplates() {
    // Update navigation
    updateActiveNavigation('report-templates');
    
    const mainContent = document.getElementById('main-content');
    if (!mainContent) return;

    if (!auth.isAuthenticated()) {
        showError('Please login to view report templates');
        return;
    }

    // Update page title with tabs
    const pageTitleElement = document.getElementById('page-title');
    if (pageTitleElement) {
        pageTitleElement.innerHTML = `
            <div class="flex items-center space-x-8">
                <button onclick="window.switchReportTemplatesTab('templates')" 
                        id="templates-tab"
                        class="text-primary-600 border-b-2 border-primary-500 pb-1 text-xl font-semibold">
                    ${t('reportTemplates.title')}
                </button>
                <button onclick="window.switchReportTemplatesTab('standard-texts')" 
                        id="standard-texts-tab"
                        class="text-gray-500 hover:text-gray-700 pb-1 text-xl font-medium">
                    ${t('reportTemplates.standardTexts')}
                </button>
                <button onclick="window.switchReportTemplatesTab('tags')" 
                        id="tags-tab"
                        class="text-gray-500 hover:text-gray-700 pb-1 text-xl font-medium">
                    ${t('reportTemplates.tags')}
                </button>
            </div>
        `;
    }

    mainContent.innerHTML = `
        <div class="h-[calc(100vh-4rem)] relative">
            <!-- Templates View -->
            <div id="templates-view" class="absolute inset-0 flex overflow-hidden bg-gray-50">
                <!-- Left Column: Templates List (sidebar width) -->
                <div class="w-64 bg-white border-r border-gray-200 flex flex-col">
                <!-- Add Template Button -->
                <div class="p-4 border-b border-gray-200">
                    <button type="button" onclick="createReportTemplate()" 
                            class="w-full inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 shadow-sm">
                        <svg class="-ml-1 mr-2 h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
                        </svg>
                        ${t('reportTemplates.addTemplate')}
                    </button>
                    
                    <!-- Copy Template Button (shown when template selected) -->
                    <button type="button" onclick="copyReportTemplate()" 
                            id="copy-template-btn"
                            class="hidden w-full mt-2 inline-flex items-center justify-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 shadow-sm">
                        <svg class="-ml-1 mr-2 h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                        </svg>
                        ${t('reportTemplates.copyTemplate')}
                    </button>
                </div>

                <!-- Search Bar -->
                <div class="p-4 border-b border-gray-200">
                    <input type="text" 
                           id="template-search" 
                           placeholder="${t('reportTemplates.searchPlaceholder')}" 
                           class="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                           oninput="filterTemplates()">
                </div>

                <!-- Templates List -->
                <div class="flex-1 overflow-y-auto">
                    <div id="templates-list" class="divide-y divide-gray-200">
                        <!-- Templates will be populated here -->
                        <div class="p-4 text-center text-sm text-gray-500">
                            <div class="animate-pulse">${t('reportTemplates.loadingTemplates')}</div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Middle Column: Report Canvas (flexible width) -->
            <div class="flex-1 bg-gray-50 flex flex-col overflow-hidden">
                <!-- Canvas Header -->
                <div class="bg-white border-b border-gray-200 px-6 py-4">
                    <div class="flex items-center justify-between">
                        <div>
                            <h2 id="canvas-title" class="text-lg font-semibold text-gray-900">${t('reportTemplates.selectTemplate')}</h2>
                            <p id="canvas-subtitle" class="mt-1 text-sm text-gray-500">${t('reportTemplates.selectTemplateSubtitle')}</p>
                        </div>
                        <div class="flex items-center space-x-2">
                            <button type="button" onclick="deleteTemplate()" 
                                    class="inline-flex items-center px-3 py-2 border border-red-300 text-sm font-medium rounded-md text-red-700 bg-white hover:bg-red-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                    id="delete-template-btn" disabled>
                                <svg class="-ml-1 mr-2 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                </svg>
                                ${t('reportTemplates.delete')}
                            </button>
                            <button type="button" onclick="saveTemplate()" 
                                    class="inline-flex items-center px-3 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                    id="save-template-btn" disabled>
                                <svg class="-ml-1 mr-2 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4" />
                                </svg>
                                ${t('reportTemplates.save')}
                            </button>
                            <button type="button" onclick="releaseTemplate()" 
                                    class="inline-flex items-center px-3 py-2 border border-green-600 text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
                                    id="release-template-btn" disabled>
                                <svg class="-ml-1 mr-2 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                                ${t('reportTemplates.release')}
                            </button>
                            <button type="button" onclick="previewTemplate()" 
                                    class="inline-flex items-center px-3 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                    id="preview-template-btn" disabled>
                                <svg class="-ml-1 mr-2 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                </svg>
                                ${t('reportTemplates.preview')}
                            </button>
                        </div>
                    </div>
                    
                    <!-- Page Tabs (shown when template selected) -->
                    <div id="page-tabs" class="hidden mt-4 border-b border-gray-200">
                        <nav class="-mb-px flex space-x-8" aria-label="Tabs">
                            <button onclick="switchReportPage('frontpage')" 
                                    class="page-tab border-primary-500 text-primary-600 whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm"
                                    data-page="frontpage">
                                ${t('reportTemplates.frontPage')}
                            </button>
                            <button onclick="switchReportPage('results')" 
                                    class="page-tab border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm"
                                    data-page="results">
                                ${t('reportTemplates.results')}
                            </button>
                            <button onclick="switchReportPage('data')" 
                                    class="page-tab border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm"
                                    data-page="data">
                                ${t('reportTemplates.documentation')}
                            </button>
                            <button onclick="switchReportPage('conclusion')" 
                                    class="page-tab border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm"
                                    data-page="conclusion">
                                ${t('reportTemplates.finalRemarks')}
                            </button>
                        </nav>
                    </div>
                </div>

                <!-- Canvas Area -->
                <div class="flex-1 overflow-y-auto p-6 relative">
                    <!-- Empty State (shown when no template selected) -->
                    <div id="empty-state" class="absolute inset-0 flex items-center justify-center">
                        <div class="text-center">
                            <svg class="mx-auto h-16 w-16 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                            </svg>
                            <h3 class="mt-4 text-lg font-medium text-gray-900">${t('reportTemplates.noTemplateSelected')}</h3>
                            <p class="mt-2 text-sm text-gray-500">${t('reportTemplates.getStarted')}</p>
                        </div>
                    </div>
                    
                    <!-- Report Canvas (hidden until template selected) -->
                    <div id="report-canvas" class="hidden bg-white rounded-lg shadow-lg min-h-[800px] max-w-[210mm] mx-auto border border-gray-300">
                        <!-- Front Page Layout -->
                        <div id="canvas-frontpage" class="page-canvas p-8">
                            <!-- Header (split left/right) -->
                            <div class="border-b border-gray-300 pb-4 mb-4">
                                <div class="grid grid-cols-2 gap-4">
                                    <div class="border-2 border-dashed border-gray-300 rounded p-4 min-h-[80px]" data-drop-zone="header-left">
                                        <p class="text-xs text-gray-400">Header Left</p>
                                    </div>
                                    <div class="border-2 border-dashed border-gray-300 rounded p-4 min-h-[80px]" data-drop-zone="header-right">
                                        <p class="text-xs text-gray-400">Header Right</p>
                                    </div>
                                </div>
                            </div>
                            
                            <!-- Main Content Area -->
                            <div class="mb-4">
                                <!-- Main Content -->
                                <div class="border-2 border-dashed border-gray-300 rounded p-4 mb-4">
                                    <p class="text-xs text-gray-400 mb-3">Main Content</p>
                                    <div class="min-h-[300px] mb-4 border-2 border-dashed border-gray-200 rounded p-3" data-drop-zone="main-content">
                                        <p class="text-xs text-gray-300">Drop element here</p>
                                    </div>
                                    
                                    <!-- Two Headlines Side-by-Side -->
                                    <div class="grid grid-cols-2 gap-4">
                                        <div class="border-2 border-dashed border-gray-200 rounded p-3 min-h-[100px]" data-drop-zone="headline-left">
                                            <p class="text-xs text-gray-300">Headline Left</p>
                                        </div>
                                        <div class="border-2 border-dashed border-gray-200 rounded p-3 min-h-[100px]" data-drop-zone="headline-right">
                                            <p class="text-xs text-gray-300">Headline Right</p>
                                        </div>
                                    </div>
                                </div>
                                
                                <!-- Details Section - 4 Boxes in 2x2 Grid -->
                                <div class="border-2 border-dashed border-gray-300 rounded p-4">
                                    <p class="text-xs text-gray-400 mb-3">Details</p>
                                    <div class="grid grid-cols-2 gap-4">
                                        <div class="border-2 border-dashed border-gray-200 rounded p-3 min-h-[100px]" data-drop-zone="detail-1">
                                            <p class="text-xs text-gray-300">Detail 1</p>
                                        </div>
                                        <div class="border-2 border-dashed border-gray-200 rounded p-3 min-h-[100px]" data-drop-zone="detail-2">
                                            <p class="text-xs text-gray-300">Detail 2</p>
                                        </div>
                                        <div class="border-2 border-dashed border-gray-200 rounded p-3 min-h-[100px]" data-drop-zone="detail-3">
                                            <p class="text-xs text-gray-300">Detail 3</p>
                                        </div>
                                        <div class="border-2 border-dashed border-gray-200 rounded p-3 min-h-[100px]" data-drop-zone="detail-4">
                                            <p class="text-xs text-gray-300">Detail 4</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            
                            <!-- Footer (split left/right) -->
                            <div class="border-t border-gray-300 pt-4 mt-4">
                                <div class="grid grid-cols-2 gap-4">
                                    <div class="border-2 border-dashed border-gray-300 rounded p-4 min-h-[60px]" data-drop-zone="footer-left">
                                        <p class="text-xs text-gray-400">Footer Left</p>
                                    </div>
                                    <div class="border-2 border-dashed border-gray-300 rounded p-4 min-h-[60px]" data-drop-zone="footer-right">
                                        <p class="text-xs text-gray-400">Footer Right</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                        
                        <!-- Results Page Layout -->
                        <div id="canvas-results" class="page-canvas hidden p-8">
                            <!-- Top Section (smaller) -->
                            <div class="border-2 border-dashed border-gray-300 rounded p-4 min-h-[100px] mb-4" data-drop-zone="results-top">
                                <p class="text-xs text-gray-400">Results Top</p>
                            </div>
                            
                            <!-- Upper Main Section -->
                            <div class="border-2 border-dashed border-gray-300 rounded p-4 min-h-[200px] mb-4" data-drop-zone="results-upper">
                                <p class="text-xs text-gray-400">Results Upper</p>
                            </div>
                            
                            <!-- Middle Main Section -->
                            <div class="border-2 border-dashed border-gray-300 rounded p-4 min-h-[200px] mb-4" data-drop-zone="results-middle">
                                <p class="text-xs text-gray-400">Results Middle</p>
                            </div>
                            
                            <!-- Lower Main Section -->
                            <div class="border-2 border-dashed border-gray-300 rounded p-4 min-h-[200px] mb-4" data-drop-zone="results-lower">
                                <p class="text-xs text-gray-400">Results Lower</p>
                            </div>
                            
                            <!-- Bottom Section (smaller) -->
                            <div class="border-2 border-dashed border-gray-300 rounded p-4 min-h-[100px]" data-drop-zone="results-bottom">
                                <p class="text-xs text-gray-400">Results Bottom</p>
                            </div>
                        </div>
                        
                        <!-- Report Data Page Layout -->
                        <div id="canvas-data" class="page-canvas hidden p-8">
                            <!-- Structured Layout (original) -->
                            <div id="structured-layout" class="structured-documentation-layout">
                                <!-- Documentation Section -->
                                <div class="border-2 border-dashed border-gray-300 rounded p-4 mb-4 min-h-[200px]" data-drop-zone="data-collection">
                                    <p class="text-xs text-gray-400 mb-3">Documentation</p>
                                </div>
                                
                                <!-- Structure Section (80% height) -->
                                <div class="border-2 border-dashed border-gray-300 rounded p-4 min-h-[600px]">
                                    <div class="flex items-center justify-between mb-3">
                                        <p class="text-xs text-gray-400">Structure</p>
                                        <button onclick="openStructureBuilderForDataPage()" 
                                                class="inline-flex items-center px-3 py-1.5 text-xs font-medium rounded border border-gray-300 text-gray-700 bg-white hover:bg-gray-50">
                                            <svg class="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 12h16M4 18h7" />
                                            </svg>
                                            <span id="structure-button-text">Add Structure</span>
                                        </button>
                                    </div>
                                    <div id="structure-preview" class="text-xs text-gray-500">
                                        Optional: Define hierarchical categories to organize documentation
                                    </div>
                                </div>
                            </div>
                            
                            <!-- Dynamic Layout (new) -->
                            <div id="dynamic-layout" class="dynamic-documentation-layout hidden">
                                <div class="mb-4 p-4 bg-blue-50 border border-blue-200 rounded">
                                    <p class="text-sm text-blue-800 font-medium mb-1">Dynamic Documentation Template</p>
                                    <p class="text-xs text-blue-600">Add input elements below. Each element becomes a documentation entry template that users can fill out.</p>
                                </div>
                                
                                <!-- Dynamic Entry Templates Container -->
                                <div id="dynamic-entries-container" class="space-y-4">
                                    <!-- Entry blocks will be added here -->
                                </div>
                                
                                <!-- Add Entry Block Button -->
                                <button onclick="addDynamicEntryBlock()" 
                                        class="w-full mt-4 inline-flex items-center justify-center px-4 py-3 border-2 border-dashed border-gray-300 text-sm font-medium rounded-md text-gray-600 bg-white hover:border-primary-500 hover:text-primary-600 hover:bg-primary-50 transition-colors">
                                    <svg class="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                                    </svg>
                                    Add Entry Template
                                </button>
                            </div>
                        </div>
                        
                        <!-- Conclusion Page Layout -->
                        <div id="canvas-conclusion" class="page-canvas hidden p-8">
                            <div class="border-2 border-dashed border-gray-300 rounded p-4 min-h-[700px]">
                                <p class="text-xs text-gray-400 mb-2">Conclusion Page</p>
                                <p class="text-xs text-gray-500">Final remarks and conclusions (typically fixed text)</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Right Column: Element Types (sidebar width) -->
            <div id="elements-palette" class="w-64 bg-white border-l border-gray-200 flex flex-col overflow-y-auto">
                <!-- Content will be dynamically generated based on current page -->
            </div>
        </div>
        <!-- End of Templates View -->

        <!-- Standard Texts View (initially hidden) -->
        <div id="standard-texts-view" style="display: none;" class="absolute inset-0 overflow-hidden bg-gray-50">
            <!-- Content will be populated by standard-texts.js -->
        </div>

        <!-- Tags View (initially hidden) -->
        <div id="tags-view" style="display: none;" class="absolute inset-0 overflow-hidden bg-gray-50">
            <!-- Content will be populated by tags.js -->
        </div>
        </div>
    `;

    // Initialize the elements palette for the current page
    updateElementsPalette('frontpage');
    
    // Initialize drag and drop
    initializeDragAndDrop();
    
    // Add beforeunload handler to warn about unsaved changes
    window.addEventListener('beforeunload', handleBeforeUnload);
    
    // Load templates from database
    await loadTemplates();
}

/**
 * Handle beforeunload event to warn about unsaved changes
 */
function handleBeforeUnload(e) {
    if (reportTemplatesState.hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue = ''; // Chrome requires returnValue to be set
        return ''; // Some browsers show this message
    }
}

/**
 * Switch between Report Templates tabs (Templates vs Standard Texts vs Tags)
 */
window.switchReportTemplatesTab = async function(tab) {
    // Check for unsaved changes when switching tabs
    if (reportTemplatesState.hasUnsavedChanges && tab !== 'templates') {
        const result = await showUnsavedChangesDialog();
        
        if (result === 'cancel') {
            return; // Stay on current tab
        } else if (result === 'save') {
            await saveTemplate();
        }
        // If 'discard', continue with tab switch
        
        reportTemplatesState.hasUnsavedChanges = false;
        updateSaveButtonState();
    }
    
    const templatesTab = document.getElementById('templates-tab');
    const standardTextsTab = document.getElementById('standard-texts-tab');
    const tagsTab = document.getElementById('tags-tab');
    
    if (templatesTab && standardTextsTab && tagsTab) {
        if (tab === 'templates') {
            templatesTab.className = 'text-primary-600 border-b-2 border-primary-500 pb-1 text-xl font-semibold';
            standardTextsTab.className = 'text-gray-500 hover:text-gray-700 pb-1 text-xl font-medium';
            tagsTab.className = 'text-gray-500 hover:text-gray-700 pb-1 text-xl font-medium';
        } else if (tab === 'standard-texts') {
            templatesTab.className = 'text-gray-500 hover:text-gray-700 pb-1 text-xl font-medium';
            standardTextsTab.className = 'text-primary-600 border-b-2 border-primary-500 pb-1 text-xl font-semibold';
            tagsTab.className = 'text-gray-500 hover:text-gray-700 pb-1 text-xl font-medium';
        } else if (tab === 'tags') {
            templatesTab.className = 'text-gray-500 hover:text-gray-700 pb-1 text-xl font-medium';
            standardTextsTab.className = 'text-gray-500 hover:text-gray-700 pb-1 text-xl font-medium';
            tagsTab.className = 'text-primary-600 border-b-2 border-primary-500 pb-1 text-xl font-semibold';
        }
    }
    
    // Show/hide views
    const templatesView = document.getElementById('templates-view');
    const standardTextsView = document.getElementById('standard-texts-view');
    const tagsView = document.getElementById('tags-view');
    
    if (tab === 'templates') {
        if (templatesView) {
            templatesView.style.display = 'flex';
        }
        if (standardTextsView) {
            standardTextsView.style.display = 'none';
        }
        if (tagsView) {
            tagsView.style.display = 'none';
        }
    } else if (tab === 'standard-texts') {
        if (templatesView) {
            templatesView.style.display = 'none';
        }
        if (standardTextsView) {
            standardTextsView.style.display = 'block';
        }
        if (tagsView) {
            tagsView.style.display = 'none';
        }
        // Load standard texts if not already loaded
        showStandardTexts();
    } else if (tab === 'tags') {
        if (templatesView) {
            templatesView.style.display = 'none';
        }
        if (standardTextsView) {
            standardTextsView.style.display = 'none';
        }
        if (tagsView) {
            tagsView.style.display = 'block';
        }
        // Load tags if not already loaded
        showTags();
    }
}

/**
 * Load templates from the database
 */
async function loadTemplates() {
    try {
        const currentUser = getCurrentUser();
        if (!currentUser || !currentUser.account_id) {
            console.warn('No account_id found for current user');
            return;
        }

        // Only load active templates by default
        const templates = await getReportTemplates({ 
            account_id: currentUser.account_id,
            is_active: true 
        });
        reportTemplatesState.templates = templates;

        const templatesList = document.getElementById('templates-list');
        if (!templatesList) return;

        if (templates.length === 0) {
            templatesList.innerHTML = `
                <div class="p-4 text-center text-sm text-gray-500">
                    No templates yet.<br>Click "Add Template" to create one.
                </div>
            `;
            return;
        }

        // Render templates
        templatesList.innerHTML = templates.map(template => {
            // Determine icon and color based on report type
            let icon, iconColor, typeLabel;
            const reportType = template.report_type || 'data_collection';
            
            if (reportType === 'filter') {
                icon = 'M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z';
                iconColor = 'text-blue-600';
                typeLabel = 'Filter';
            } else if (reportType === 'statistics') {
                icon = 'M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z';
                iconColor = 'text-green-600';
                typeLabel = 'Statistics';
            } else {
                icon = 'M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01';
                iconColor = 'text-primary-600';
                typeLabel = 'Data Collection';
            }
            
            return `
            <div class="template-item p-3 bg-gray-100 hover:bg-gray-50 cursor-pointer ${template._id === reportTemplatesState.selectedTemplateId ? 'border-l-4 border-primary-600 bg-primary-50' : ''}" 
                 data-template-id="${template._id}"
                 onclick="loadTemplate('${template._id}')">
                <div class="flex items-start">
                    <svg class="w-5 h-5 ${iconColor} mr-2 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="${icon}" />
                    </svg>
                    <div class="flex-1 min-w-0">
                        <h4 class="text-sm font-medium text-gray-900 hover:text-primary-600" 
                            onclick="event.stopPropagation(); editTemplateName('${template._id}', '${escapeHtml(template.name).replace(/'/g, "&#39;")}')">
                            ${escapeHtml(template.name)}
                        </h4>
                        <p class="mt-1 text-xs text-gray-500">${typeLabel} • ${new Date(template.created_at).toLocaleDateString()}</p>
                    </div>
                </div>
            </div>`;
        }).join('');
    } catch (error) {
        console.error('Failed to load templates:', error);
        showError('Failed to load templates');
    }
}

/**
 * Load a specific template and its elements
 */
async function loadTemplate(templateId) {
    try {
        // Check for unsaved changes before switching templates
        if (reportTemplatesState.hasUnsavedChanges) {
            const result = await showUnsavedChangesDialog();
            
            if (result === 'cancel') {
                return; // Stay on current template
            } else if (result === 'save') {
                await saveTemplate();
            }
            // If 'discard', continue with template switch
            
            // Clear unsaved changes flag
            reportTemplatesState.hasUnsavedChanges = false;
            updateSaveButtonState();
        }
        
        // Load template elements
        const elements = await getTemplateElements(templateId);
        
        // Update state
        reportTemplatesState.selectedTemplateId = templateId;
        reportTemplatesState.elements = elements.map(el => ({
            id: el._id,
            position: el.position,
            page: el.page,
            zone: el.zone,
            elementType: el.element_type,
            introText: el.intro_text || '',
            staticData: el.static_data,
            config: el.config,
            inputRequired: el.input_required || false,
            alignment: el.alignment,
            multiEntry: el.multi_entry || false
        }));
        
        // Get template info
        const template = reportTemplatesState.templates.find(t => t._id === templateId);
        if (template) {
            reportTemplatesState.templateName = template.name;
            reportTemplatesState.templateDescription = template.description || '';
            reportTemplatesState.isReleased = template.is_released || false;
            reportTemplatesState.documentationLayout = template.documentation_layout || 'structured';
            reportTemplatesState.reportType = template.report_type || 'data_collection'; // Default to data_collection for old templates
        }
        
        // Update UI - highlight selected template
        document.querySelectorAll('.template-item').forEach(item => {
            item.classList.remove('border-l-4', 'border-primary-600', 'bg-primary-50');
        });
        const selectedItem = document.querySelector(`[data-template-id="${templateId}"]`);
        if (selectedItem) {
            selectedItem.classList.add('border-l-4', 'border-primary-600', 'bg-primary-50');
        }
        
        // Show canvas and render elements
        enableTemplateUI(templateId);
        
        // Update element palette based on report type and current page
        updateElementsPalette(reportTemplatesState.currentPage);
        
        // Show/hide tabs based on report type
        const pageTabs = document.getElementById('page-tabs');
        if (pageTabs) {
            const dataTab = pageTabs.querySelector('[data-page="data"]');
            const conclusionTab = pageTabs.querySelector('[data-page="conclusion"]');
            
            if (reportTemplatesState.reportType === 'filter' || reportTemplatesState.reportType === 'statistics') {
                // Filter and Statistics only have Frontpage and Results
                if (dataTab) dataTab.style.display = 'none';
                if (conclusionTab) conclusionTab.style.display = 'none';
            } else {
                // Data Collection has all tabs
                if (dataTab) dataTab.style.display = '';
                if (conclusionTab) conclusionTab.style.display = '';
            }
        }
        
        // Switch to appropriate documentation layout
        switchDocumentationLayout(reportTemplatesState.documentationLayout || 'structured');
        
        // For dynamic templates, recreate the dynamic blocks before rendering elements
        if (reportTemplatesState.documentationLayout === 'dynamic') {
            recreateDynamicBlocks();
        }
        
        renderTemplateElements();
        
        // Update structure preview if on data page (only for structured layout)
        updateStructurePreview();
        
        // Show copy button
        document.getElementById('copy-template-btn').classList.remove('hidden');
        
        showSuccess('Template loaded successfully');
    } catch (error) {
        console.error('Failed to load template:', error);
        showError('Failed to load template');
    }
}

/**
 * Render all template elements on the canvas
 */
function renderTemplateElements() {
    console.log('🎨 renderTemplateElements called - re-rendering all elements from state');
    console.trace('Render called from:');
    
    // Clear all drop zones first
    document.querySelectorAll('[data-drop-zone]').forEach(zone => {
        const placeholder = getZonePlaceholder(zone.dataset.dropZone);
        zone.innerHTML = `<p class="text-xs text-gray-300">${placeholder}</p>`;
    });
    
    // Render each element
    reportTemplatesState.elements.forEach(element => {
        const dropZone = document.querySelector(`[data-drop-zone="${element.zone}"]`);
        if (dropZone) {
            const elementHtml = createTemplateElement(element);
            dropZone.innerHTML = elementHtml;
        }
    });
    
    console.log('✅ Rendered', reportTemplatesState.elements.length, 'elements');
}

/**
 * Update the elements palette based on the current page
 */
function updateElementsPalette(page) {
    const palette = document.getElementById('elements-palette');
    if (!palette) return;
    
    let inputElements = [];
    let staticElements = [];
    
    // Define elements for each page type
    if (page === 'results') {
        // Results page: Show different elements based on report type
        const isFilterReport = reportTemplatesState.reportType === 'filter';
        
        if (isFilterReport) {
            inputElements = [
                { type: 'filtered-data', label: 'Filtered Data', icon: 'M3 10h18M3 14h18m-9-4v8m-7 0h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z' }
            ];
        } else {
            // Statistics and Data Collection reports
            inputElements = [
                { type: 'statistics-input', label: 'Statistics', icon: 'M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z' }
            ];
        }
        staticElements = [
            { type: 'text-block', label: 'Text', icon: 'M4 6h16M4 12h16M4 18h7' },
            { type: 'image', label: 'Image', icon: 'M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z' }
        ];
    } else if (page === 'data') {
        // Documentation page: Only inputs - Image and text, Evaluation
        inputElements = [
            { type: 'image-text-input', label: 'Image and text', icon: 'M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z' },
            { type: 'evaluation-input', label: 'Evaluation, Image and Text', icon: 'M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z' }
        ];
        staticElements = []; // No statics for documentation page
    } else {
        // Default for frontpage, conclusion
        const isFilterOrStats = reportTemplatesState.reportType === 'filter' || reportTemplatesState.reportType === 'statistics';
        
        if (isFilterOrStats && page === 'frontpage') {
            // Filter and Statistics frontpage: Show filter elements only
            inputElements = [
                { type: 'text-input', label: 'Text Input', icon: 'M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z' },
                { type: 'date-input', label: 'Date Input', icon: 'M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z' },
                { type: 'filter-date-range', label: 'Filter Date Range', icon: 'M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z' },
                { type: 'filter-location', label: 'Filter Location', icon: 'M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z' },
                { type: 'filter-asset', label: 'Filter Asset', icon: 'M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4' },
                { type: 'filter-account', label: 'Filter Account', icon: 'M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4' }
            ];
        } else {
            // Data Collection frontpage or conclusion: Show regular input elements
            inputElements = [
                { type: 'text-input', label: 'Text Input', icon: 'M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z' },
                { type: 'date-input', label: 'Date Input', icon: 'M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z' },
                { type: 'location-input', label: 'Location Select', icon: 'M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z' },
                { type: 'user-input', label: 'User Select', icon: 'M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z' },
                { type: 'tag-input', label: 'Tag Select', icon: 'M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z' },
                { type: 'image-input', label: 'Image Input', icon: 'M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z' }
            ];
        }
        staticElements = [
            { type: 'text-block', label: 'Text', icon: 'M4 6h16M4 12h16M4 18h7' },
            { type: 'heading', label: 'Heading', icon: 'M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z' },
            { type: 'image', label: 'Image', icon: 'M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z' },
            { type: 'divider', label: 'Divider', icon: 'M20 12H4' },
            { type: 'creator-name', label: 'Creator Name', icon: 'M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z' },
            { type: 'location-contacts', label: 'Location Contacts', icon: 'M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z' }
        ];
    }
    
    // Generate HTML
    let html = `
        <!-- Inputs Section -->
        <div class="border-b border-gray-200">
            <div class="px-4 py-3 bg-gray-50 border-b border-gray-200">
                <h3 class="text-sm font-semibold text-gray-700 uppercase">Inputs</h3>
            </div>
            <div class="p-3 space-y-2">
    `;
    
    inputElements.forEach(el => {
        html += `
                <div draggable="true" 
                     class="element-type cursor-move p-3 bg-white border border-gray-300 rounded-md hover:bg-gray-50 hover:border-primary-500 transition-colors"
                     data-element-type="${el.type}">
                    <div class="flex items-center">
                        <svg class="h-5 w-5 text-gray-400 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="${el.icon}" />
                        </svg>
                        <span class="text-sm font-medium text-gray-700">${el.label}</span>
                    </div>
                </div>
        `;
    });
    
    html += `
            </div>
        </div>
    `;
    
    // Only show Statics section if there are static elements
    if (staticElements.length > 0) {
        html += `
        <!-- Statics Section -->
        <div>
            <div class="px-4 py-3 bg-gray-50 border-b border-gray-200">
                <h3 class="text-sm font-semibold text-gray-700 uppercase">Statics</h3>
            </div>
            <div class="p-3 space-y-2">
        `;
        
        staticElements.forEach(el => {
            html += `
                <div draggable="true" 
                     class="element-type cursor-move p-3 bg-white border border-gray-300 rounded-md hover:bg-gray-50 hover:border-primary-500 transition-colors"
                     data-element-type="${el.type}">
                    <div class="flex items-center">
                        <svg class="h-5 w-5 text-gray-400 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="${el.icon}" />
                        </svg>
                        <span class="text-sm font-medium text-gray-700">${el.label}</span>
                    </div>
                </div>
            `;
        });
        
        html += `
            </div>
        </div>
        `;
    }
    
    palette.innerHTML = html;
    
    // Reinitialize drag handlers for new elements
    document.querySelectorAll('.element-type').forEach(element => {
        element.addEventListener('dragstart', handleDragStart);
    });
}

/**
 * Initialize drag and drop functionality
 */
function initializeDragAndDrop() {
    const elementTypes = document.querySelectorAll('.element-type');

    elementTypes.forEach(element => {
        element.addEventListener('dragstart', handleDragStart);
    });

    // Add drop handlers to all drop zones
    updateDropZones();
}

function updateDropZones() {
    const dropZones = document.querySelectorAll('[data-drop-zone]');
    
    dropZones.forEach(zone => {
        // Remove old listeners to prevent duplicates
        zone.removeEventListener('dragover', handleDragOver);
        zone.removeEventListener('drop', handleDrop);
        zone.removeEventListener('dragleave', handleDragLeave);
        zone.removeEventListener('dragenter', handleDragEnter);
        
        // Add new listeners
        zone.addEventListener('dragover', handleDragOver);
        zone.addEventListener('drop', handleDrop);
        zone.addEventListener('dragleave', handleDragLeave);
        zone.addEventListener('dragenter', handleDragEnter);
    });
}

function handleDragEnter(e) {
    e.preventDefault();
    e.currentTarget.classList.add('border-primary-500', 'bg-primary-50');
}

function handleDragLeave(e) {
    e.preventDefault();
    e.currentTarget.classList.remove('border-primary-500', 'bg-primary-50');
}

function handleDragStart(e) {
    // Check if this is a palette element or an existing template element
    const paletteElement = e.target.closest('.element-type');
    
    if (paletteElement) {
        // Dragging from palette - creating new element
        const elementType = paletteElement.dataset.elementType;
        console.log('🚀 Drag started for:', elementType);
        e.dataTransfer.effectAllowed = 'copy';
        e.dataTransfer.setData('elementType', elementType);
    }
}

function handleDragOver(e) {
    e.preventDefault();
    // Set dropEffect based on whether we're moving or copying
    const movedElementId = e.dataTransfer.types.includes('elementid');
    e.dataTransfer.dropEffect = movedElementId ? 'move' : 'copy';
}

function handleDrop(e) {
    e.preventDefault();
    e.stopPropagation();
    
    const elementType = e.dataTransfer.getData('elementType');
    const movedElementId = e.dataTransfer.getData('elementId');
    
    console.log('📦 Drop event:', { elementType, movedElementId });
    
    const dropZone = e.currentTarget;
    const zoneName = dropZone.dataset.dropZone;
    
    // Remove highlight
    dropZone.classList.remove('border-primary-500', 'bg-primary-50');
    
    // Check if drop zone already has an element
    const existingElement = dropZone.querySelector('.template-element');
    if (existingElement && !movedElementId) {
        console.warn('⚠️ Zone already has element');
        showError('This zone already contains an element. Remove it first.');
        return;
    }
    
    // Get fixed position for this zone
    let position = zonePositions[zoneName];
    
    // Handle dynamic zones (dynamic-0, dynamic-1, etc.)
    if (!position && zoneName.startsWith('dynamic-')) {
        // For dynamic zones, use position starting from 22 onwards
        const blockIndex = parseInt(zoneName.replace('dynamic-', ''));
        position = 22 + blockIndex;
    }
    
    if (!position) {
        console.error('❌ Invalid drop zone:', zoneName);
        showError('Invalid drop zone');
        return;
    }
    
    // Handle moving an existing element
    if (movedElementId) {
        const elementData = reportTemplatesState.elements.find(el => el.id === movedElementId);
        if (!elementData) {
            console.error('❌ Element not found in state:', movedElementId);
            return;
        }
        
        // Only allow moving within the same page
        if (elementData.page !== reportTemplatesState.currentPage) {
            console.warn('⚠️ Cannot move element between different pages');
            showError('Elements can only be moved within the same page');
            return;
        }
        
        // If dropping on the same zone, do nothing
        if (elementData.zone === zoneName) {
            return;
        }
        
        // Check if zone already has an element (don't allow overwriting)
        if (existingElement) {
            showError('This zone already contains an element. Remove it first.');
            return;
        }
        
        // Store old zone name before updating
        const oldZoneName = elementData.zone;
        
        // Update element data with new zone and position
        elementData.zone = zoneName;
        elementData.position = position;
        
        // Remove the dragging element's opacity class before we destroy it
        const oldElement = document.querySelector(`[data-element-id="${movedElementId}"]`);
        if (oldElement) {
            oldElement.classList.remove('opacity-50');
        }
        
        // Find and clear the old zone
        const oldZone = document.querySelector(`[data-drop-zone="${oldZoneName}"]`);
        if (oldZone) {
            oldZone.innerHTML = `<p class="text-xs text-gray-400">${getZonePlaceholder(oldZoneName)}</p>`;
        }
        
        // Create the element HTML with updated data
        const elementHtml = createTemplateElement(elementData);
        
        // Add to new zone
        dropZone.innerHTML = elementHtml;
        
        showSuccess(`Moved ${formatElementType(elementData.elementType)} to position ${position}`);
        return;
    }
    
    // Handle adding a new element from palette
    const elementData = {
        id: 'element-' + Date.now(),
        position: position,
        page: reportTemplatesState.currentPage,
        zone: zoneName,
        elementType: elementType,
        staticData: null, // Will be filled when user edits
        inputLabel: null,
        inputRequired: false,
        introText: '', // Optional intro text for all elements
        multiEntry: false // Will be set based on checkbox state
    };
    
    // For dynamic zones, check if multi-entry is enabled
    if (zoneName.startsWith('dynamic-')) {
        const blockIndex = parseInt(zoneName.replace('dynamic-', ''));
        const block = document.querySelector(`[data-block-index="${blockIndex}"]`);
        if (block) {
            const checkbox = block.querySelector('input[type="checkbox"]');
            if (checkbox) {
                elementData.multiEntry = checkbox.checked;
                console.log(`Setting multiEntry for zone ${zoneName}:`, checkbox.checked);
            }
        }
    }
    
    // Add to state
    reportTemplatesState.elements.push(elementData);
    
    // Create the element HTML with position number
    const elementHtml = createTemplateElement(elementData);
    
    // Clear placeholder text and add element
    dropZone.innerHTML = elementHtml;
    
    showSuccess(`Added ${formatElementType(elementType)} at position ${position}`);
}

/**
 * Create HTML for a template element
 */
function createTemplateElement(elementData) {
    const elementLabel = formatElementType(elementData.elementType);
    
    // Check if element needs configuration (input elements have optional config)
    const needsConfig = ['evaluation-input', 'text-block', 'heading', 'image', 'creator-name', 'location-contacts', 'statistics-input'].includes(elementData.elementType);
    const hasOptionalConfig = ['text-input', 'date-input', 'location-input', 'user-input', 'image-input', 'statistics-input', 'image-text-input', 'tag-input', 'filter-date-range', 'filter-location', 'filter-asset', 'filter-account', 'filtered-data'].includes(elementData.elementType);
    const configButton = (needsConfig || hasOptionalConfig) ? `
        <button onclick="configureElement('${elementData.id}')" 
                class="absolute top-2 right-10 bg-blue-500 hover:bg-blue-600 text-white rounded-full w-6 h-6 flex items-center justify-center shadow-sm"
                title="Configure element">
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
            </svg>
        </button>
    ` : '';
    
    // Generate intro text if present
    let introTextHtml = '';
    if (elementData.introText) {
        introTextHtml = `
            <div class="mb-2 text-xs text-gray-600 italic border-l-2 border-blue-400 pl-2">
                ${elementData.introText}
            </div>
        `;
    }
    
    // Generate preview based on element type and configuration
    let contentHtml = '';
    if (elementData.elementType === 'evaluation-input' && elementData.config) {
        contentHtml = generateEvaluationPreview(elementData.config);
    } else if (elementData.elementType === 'text-block' && elementData.staticData) {
        contentHtml = generateTextPreview(elementData.staticData.content || elementData.staticData, 'Text Block');
    } else if (elementData.elementType === 'heading' && elementData.staticData) {
        contentHtml = generateTextPreview(elementData.staticData.content || elementData.staticData, 'Heading', true);
    } else if (elementData.elementType === 'image' && elementData.staticData) {
        contentHtml = generateImagePreview(elementData.staticData.imageData || elementData.staticData);
    } else if (elementData.elementType === 'creator-name') {
        contentHtml = `
            <div class="text-sm text-gray-900 font-semibold">[Report Creator's Name]</div>
            <div class="text-xs text-gray-500 mt-1">Automatically populated when report is created</div>
            ${!elementData.introText ? '<div class="text-xs text-orange-600 mt-1">⚠ Add label text in configuration</div>' : ''}
        `;
    } else if (elementData.elementType === 'location-contacts') {
        contentHtml = `
            <div class="text-sm text-gray-700">
                <div class="mb-1">• Contact Person 1</div>
                <div>• Contact Person 2</div>
            </div>
            <div class="text-xs text-gray-500 mt-1">Automatically populated from location's account contacts</div>
            ${!elementData.introText ? '<div class="text-xs text-orange-600 mt-1">⚠ Add label text in configuration</div>' : ''}
        `;
    } else if (elementData.elementType === 'statistics-input') {
        const config = elementData.config || {};
        const hasConfig = config.statisticsType && config.displayFormat;
        const typeLabel = hasConfig ? getStatisticsTypeLabel(config.statisticsType) : 'Not configured';
        const formatLabel = hasConfig && config.displayFormat === 'icon' ? 'Icon Display' : 'Number Display';
        
        contentHtml = `
            <div class="text-sm text-gray-700">${typeLabel}${hasConfig ? ' - ' + formatLabel : ''}</div>
            <div class="text-xs text-gray-500 mt-1">Automatically populated when report is created</div>
            ${!hasConfig ? '<div class="text-xs text-orange-600 mt-1">⚠ Needs configuration</div>' : ''}
        `;
    } else {
        // For input elements
        const requiredBadge = elementData.inputRequired ? '<span class="ml-2 text-xs bg-red-100 text-red-600 px-1.5 py-0.5 rounded">Required</span>' : '';
        
        // Check for tag-input visibility flag
        const hiddenBadge = (elementData.elementType === 'tag-input' && elementData.config?.visibleInReport === false) 
            ? '<span class="ml-2 text-xs bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded">Hidden in Reports</span>' 
            : '';
        
        contentHtml = `
            <div class="text-sm font-medium text-gray-900 mb-2">${elementLabel}${requiredBadge}${hiddenBadge}</div>
            <div class="text-xs text-gray-500">
                ${getElementPlaceholder(elementData.elementType)}
                ${needsConfig ? '<br><span class="text-orange-600">⚠ Needs configuration</span>' : ''}
            </div>
        `;
    }
    
    return `
        <div class="template-element relative bg-white border border-gray-300 rounded p-3 cursor-move" 
             data-element-id="${elementData.id}" 
             data-element-type="${elementData.elementType}"
             data-position="${elementData.position}"
             draggable="true"
             ondragstart="handleElementDragStart(event)"
             ondragend="handleElementDragEnd(event)">
            <!-- Position Badge -->
            <div class="absolute -top-2 -left-2 bg-primary-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold shadow-sm">
                ${elementData.position}
            </div>
            
            <!-- Move Icon -->
            <div class="absolute top-2 left-2 text-gray-400" title="Drag to move">
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
                </svg>
            </div>
            
            ${configButton}
            
            <!-- Delete Button -->
            <button onclick="removeTemplateElement('${elementData.id}')" 
                    class="absolute -top-2 -right-2 bg-red-500 hover:bg-red-600 text-white rounded-full w-6 h-6 flex items-center justify-center shadow-sm"
                    title="Remove element">
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
            </button>
            
            ${introTextHtml}
            ${contentHtml}
        </div>
    `;
}

/**
 * Generate preview HTML for evaluation scale
 */
function generateEvaluationPreview(config) {
    const { scaleMin, scaleMax, redRangeMax, greenRangeMin, allowNotRelevant } = config;
    
    // Calculate yellow range
    const yellowMin = redRangeMax + 1;
    const yellowMax = greenRangeMin - 1;
    const hasYellow = yellowMin <= yellowMax;
    
    let html = `
        <div class="flex items-center gap-1 flex-wrap">
    `;
    
    // Add "Not Relevant" option if enabled
    if (allowNotRelevant) {
        html += `
            <label class="inline-flex items-center">
                <input type="radio" disabled class="w-3 h-3 text-gray-400 border-gray-300">
                <span class="ml-0.5 text-xs text-gray-600">-</span>
            </label>
        `;
    }
    
    // Generate scale options with color coding
    for (let i = scaleMin; i <= scaleMax; i++) {
        let colorClass, bgColorClass;
        
        if (i <= redRangeMax) {
            colorClass = 'text-red-600 border-red-600';
            bgColorClass = 'bg-red-50';
        } else if (hasYellow && i >= yellowMin && i <= yellowMax) {
            colorClass = 'text-yellow-600 border-yellow-600';
            bgColorClass = 'bg-yellow-50';
        } else if (i >= greenRangeMin) {
            colorClass = 'text-green-600 border-green-600';
            bgColorClass = 'bg-green-50';
        }
        
        html += `
            <label class="inline-flex items-center ${bgColorClass} px-1 py-0.5 rounded">
                <input type="radio" disabled class="w-3 h-3 ${colorClass}">
                <span class="ml-0.5 text-xs font-medium ${colorClass}">${i}</span>
            </label>
        `;
    }
    
    html += `
        </div>
    `;
    
    return html;
}

/**
 * Generate preview HTML for text/heading
 */
function generateTextPreview(text, label, isHeading = false) {
    const preview = text.length > 100 ? text.substring(0, 100) + '...' : text;
    const textClass = isHeading ? 'text-sm font-bold text-gray-900' : 'text-xs text-gray-700';
    
    return `
        <div class="text-xs font-medium text-gray-600 mb-1">${label}</div>
        <div class="${textClass} italic">"${preview}"</div>
    `;
}

/**
 * Generate preview HTML for image
 */
function generateImagePreview(imageData) {
    // imageData contains either base64 data or filename
    return `
        <div class="text-xs font-medium text-gray-600 mb-2">Image</div>
        <div class="bg-gray-100 border border-gray-300 rounded p-2 flex items-center justify-center" style="min-height: 80px;">
            ${imageData.startsWith('data:image') ? 
                `<img src="${imageData}" alt="Preview" class="max-h-20 max-w-full object-contain">` :
                `<div class="text-center">
                    <svg class="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                    <div class="text-xs text-gray-500 mt-1">${imageData}</div>
                </div>`
            }
        </div>
    `;
}

/**
 * Format element type for display
 */
function formatElementType(elementType) {
    const typeMap = {
        'text-input': 'Text Input',
        'date-input': 'Date Input',
        'location-input': 'Location Select',
        'user-input': 'User Select',
        'tag-input': 'Tag Select',
        'image-input': 'Image Input',
        'filter-date-range': 'Filter Date Range',
        'filter-location': 'Filter Location',
        'filter-asset': 'Filter Asset',
        'filter-account': 'Filter Account',
        'filtered-data': 'Filtered Data',
        'statistics-input': 'Statistics',
        'evaluation-input': 'Evaluation, Image and Text',
        'image-text-input': 'Image and Text',
        'number-input': 'Number Input',
        'dropdown-input': 'Dropdown',
        'text-block': 'Text Block',
        'heading': 'Heading',
        'image': 'Image',
        'divider': 'Divider',
        'creator-name': 'Creator Name',
        'location-contacts': 'Location Contacts',
        'table': 'Table'
    };
    return typeMap[elementType] || elementType;
}

/**
 * Get placeholder content for element type
 */
function getElementPlaceholder(elementType) {
    const placeholders = {
        'text-input': 'Single line text field',
        'date-input': 'Date picker field',
        'location-input': 'Single or multiple location selection',
        'user-input': 'Single or multiple user selection',
        'tag-input': 'Tag selection from category',
        'image-input': 'Image upload field',
        'filter-date-range': 'Date range from report context (auto-populated)',
        'filter-location': 'Location(s) from report context (auto-populated)',
        'filter-asset': 'Asset(s) from report context (auto-populated)',
        'filter-account': 'Account from report context (auto-populated)',
        'filtered-data': 'Table with filtered data from current view (auto-populated)',
        'statistics-input': 'Numeric statistics',
        'evaluation-input': 'Rating scale',
        'image-text-input': 'Image with description',
        'number-input': 'Numeric input field',
        'dropdown-input': 'Select from options',
        'text-block': 'Multi-line text content',
        'heading': 'Section heading',
        'image': 'Image placeholder',
        'divider': 'Horizontal line',
        'creator-name': 'Report creator\'s full name (auto-populated)',
        'location-contacts': 'Contact persons from location\'s account (auto-populated)',
        'table': 'Data table'
    };
    return placeholders[elementType] || 'Element content';
}

/**
 * Remove a template element
 */
function removeTemplateElement(elementId) {
    const element = document.querySelector(`[data-element-id="${elementId}"]`);
    if (!element) return;
    
    const dropZone = element.closest('[data-drop-zone]');
    const zoneName = dropZone?.dataset.dropZone || 'zone';
    
    // Remove from state
    const index = reportTemplatesState.elements.findIndex(el => el.id === elementId);
    if (index !== -1) {
        reportTemplatesState.elements.splice(index, 1);
    }
    
    // Restore placeholder text
    const placeholderText = getZonePlaceholder(zoneName);
    dropZone.innerHTML = `<p class="text-xs text-gray-300">${placeholderText}</p>`;
    
    showSuccess('Element removed');
}

/**
 * Configure an element
 */
function configureElement(elementId) {
    const elementData = reportTemplatesState.elements.find(el => el.id === elementId);
    if (!elementData) return;
    
    if (elementData.elementType === 'evaluation-input') {
        configureEvaluationElement(elementId, elementData);
    } else if (elementData.elementType === 'text-block') {
        configureTextElement(elementId, elementData, 'Text Block', 'textarea');
    } else if (elementData.elementType === 'heading') {
        configureTextElement(elementId, elementData, 'Heading', 'input');
    } else if (elementData.elementType === 'creator-name') {
        configureCreatorNameElement(elementId, elementData);
    } else if (elementData.elementType === 'location-contacts') {
        configureLocationContactsElement(elementId, elementData);
    } else if (elementData.elementType === 'image') {
        configureImageElement(elementId, elementData);
    } else if (elementData.elementType === 'statistics-input') {
        configureStatisticsElement(elementId, elementData);
    } else if (elementData.elementType === 'tag-input') {
        configureTagInputElement(elementId, elementData);
    } else if (['filter-date-range', 'filter-location', 'filter-asset', 'filter-account', 'filtered-data'].includes(elementData.elementType)) {
        configureFilterElement(elementId, elementData);
    } else if (['text-input', 'date-input', 'location-input', 'user-input', 'image-input', 'image-text-input'].includes(elementData.elementType)) {
        configureInputElement(elementId, elementData);
    }
}

/**
 * Open structure builder modal (reusable for template defaults and location overrides)
 * 
 * Structure Data Model:
 * {
 *   structure: [
 *     {
 *       id: 1,                    // Unique node ID
 *       name: "Basement",         // Category name
 *       children: [               // Child categories (max depth 3)
 *         {
 *           id: 2,
 *           name: "Toilets",
 *           children: []          // Endpoints have empty children array
 *         }
 *       ]
 *     }
 *   ]
 * }
 * 
 * When creating documentation entries (reports):
 * - Each documentation item should store: structure_node_id (int) or structure_path (string)
 * - Example: { structure_node_id: 2, text: "...", images: [...], evaluation: 8 }
 * - Or: { structure_path: "Basement > Toilets", text: "...", images: [...], evaluation: 8 }
 * - This allows grouping documentation items by structure category when displaying reports
 */
function openStructureBuilder(elementId, elementData, options = {}) {
    const config = elementData.config || {};
    const structure = config.structure || [];
    const title = options.title || 'Configure Documentation Structure';
    const subtitle = options.subtitle || 'Define hierarchical categories for documentation items (max 3 levels)';
    
    const modal = document.createElement('div');
    modal.className = 'modal fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50';
    modal.innerHTML = `
        <style>
            .structure-node.dragging {
                opacity: 0.4;
            }
            .structure-node.drag-over .node-item {
                border-top: 3px solid #3b82f6 !important;
                margin-top: 2px;
            }
            .drag-handle {
                cursor: grab;
            }
            .drag-handle:active {
                cursor: grabbing;
            }
        </style>
        <div class="relative mx-auto w-full max-w-4xl shadow-lg rounded-md bg-white" style="height: 80vh; display: flex; flex-direction: column;">
            <div class="px-5 py-4 border-b border-gray-200">
                <h3 class="text-lg font-medium text-gray-900">${escapeHtml(title)}</h3>
                <p class="mt-1 text-sm text-gray-500">${escapeHtml(subtitle)}</p>
            </div>
            
            <div style="flex: 1; display: grid; grid-template-columns: 1fr 1fr; gap: 1rem; padding: 1.25rem; overflow: hidden;">
                <!-- Left Panel: Tree View -->
                <div style="display: flex; flex-direction: column; gap: 0.75rem; overflow: hidden;">
                    <div class="flex items-center justify-between">
                        <h4 class="text-sm font-medium text-gray-700">Structure Tree</h4>
                        <button onclick="addRootNode()" class="inline-flex items-center px-2 py-1 text-xs font-medium rounded border border-gray-300 text-gray-700 bg-white hover:bg-gray-50">
                            <svg class="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
                            </svg>
                            Add Root
                        </button>
                    </div>
                    <div id="structure-tree" class="flex-1 overflow-y-auto border border-gray-200 rounded p-3 bg-gray-50" style="min-height: 0;">
                        <!-- Tree will be rendered here -->
                    </div>
                </div>
                
                <!-- Right Panel: Edit Form -->
                <div style="display: flex; flex-direction: column; gap: 0.75rem; overflow: hidden;">
                    <h4 class="text-sm font-medium text-gray-700">Edit Node</h4>
                    <div id="edit-panel" class="flex-1 overflow-y-auto border border-gray-200 rounded p-4 bg-white" style="min-height: 0;">
                        <div class="text-sm text-gray-500 text-center py-8">
                            Select a node to edit or add a new root node to get started
                        </div>
                    </div>
                </div>
            </div>
            
            <div class="px-5 py-4 border-t border-gray-200 flex justify-between items-center">
                <div class="text-xs text-gray-500">
                    Examples: "Basement > Toilets" or flat: "Inside", "Outside"
                </div>
                <div class="flex space-x-3">
                    <button onclick="closeStructureBuilder()" class="px-4 py-2 bg-gray-200 text-gray-800 text-sm font-medium rounded-md hover:bg-gray-300">
                        Cancel
                    </button>
                    <button onclick="saveStructure('${elementId}')" class="px-4 py-2 bg-primary-600 text-white text-sm font-medium rounded-md hover:bg-primary-700">
                        Save Structure
                    </button>
                </div>
            </div>
        </div>
    `;
    
    document.body.appendChild(modal);
    modal.id = 'structure-builder-modal';
    
    // Initialize state
    window.structureBuilderState = {
        elementId: elementId,
        structure: JSON.parse(JSON.stringify(structure)), // Deep clone
        selectedNodeId: null,
        nextNodeId: structure.length > 0 ? Math.max(...getAllNodeIds(structure)) + 1 : 1,
        draggedNodeId: null,
        dragOverNodeId: null
    };
    
    // Render initial tree
    renderStructureTree();
}

/**
 * Get all node IDs from structure (recursive)
 */
function getAllNodeIds(nodes) {
    let ids = [];
    nodes.forEach(node => {
        ids.push(node.id);
        if (node.children && node.children.length > 0) {
            ids = ids.concat(getAllNodeIds(node.children));
        }
    });
    return ids;
}

/**
 * Render structure tree
 */
function renderStructureTree() {
    const container = document.getElementById('structure-tree');
    if (!container) return;
    
    const state = window.structureBuilderState;
    
    if (state.structure.length === 0) {
        container.innerHTML = '<div class="text-sm text-gray-400 text-center py-4">No structure defined yet<br>Click "Add Root" to start</div>';
        return;
    }
    
    container.innerHTML = renderNodes(state.structure, 0);
}

/**
 * Render nodes recursively
 */
function renderNodes(nodes, depth, parentId = null) {
    return nodes.map((node, index) => {
        const state = window.structureBuilderState;
        const isSelected = state.selectedNodeId === node.id;
        const hasChildren = node.children && node.children.length > 0;
        const canAddChild = depth < 2; // Max depth 3 (0, 1, 2)
        const isDragging = node.id === state.draggedNodeId;
        const isDragOver = node.id === state.dragOverNodeId;
        
        return `
            <div class="structure-node ${isDragging ? 'dragging' : ''} ${isDragOver ? 'drag-over' : ''}" 
                 data-node-id="${node.id}" 
                 data-depth="${depth}"
                 data-parent-id="${parentId || ''}"
                 data-index="${index}"
                 draggable="true"
                 ondragstart="handleNodeDragStart(event, ${node.id})"
                 ondragover="handleNodeDragOver(event, ${node.id})"
                 ondrop="handleNodeDrop(event, ${node.id}, ${parentId || 'null'})"
                 ondragend="handleNodeDragEnd(event)"
                 ondragleave="handleNodeDragLeave(event)">
                <div class="node-item ${isSelected ? 'selected' : ''}" style="margin-left: ${depth * 20}px; padding: 0.5rem; border-radius: 0.25rem; margin-bottom: 0.25rem; background: white; border: 1px solid ${isSelected ? '#2563eb' : '#e5e7eb'}; cursor: pointer; display: flex; align-items: center; justify-content: space-between;" onclick="selectNode(${node.id})">
                    <div class="node-content" style="display: flex; align-items: center; gap: 0.5rem; flex: 1;">
                        <span class="drag-handle" style="cursor: grab; color: #9ca3af; font-size: 1rem; user-select: none; padding: 0 0.25rem;" title="Drag to reorder">⋮⋮</span>
                        ${hasChildren ? 
                            `<svg class="w-3 h-3 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
                            </svg>` :
                            `<div style="width: 12px;"></div>`
                        }
                        <span class="node-name" style="font-size: 0.875rem; color: #374151;">${escapeHtml(node.name)}</span>
                    </div>
                    <div class="node-actions" style="display: flex; gap: 0.25rem;">
                        ${canAddChild ? `
                            <button onclick="event.stopPropagation(); addChildNode(${node.id})" class="node-action-btn" title="Add child" style="padding: 0.25rem; border-radius: 0.25rem; color: #6b7280; hover:background: #f3f4f6;">
                                <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
                                </svg>
                            </button>
                        ` : ''}
                        <button onclick="event.stopPropagation(); deleteNode(${node.id})" class="node-action-btn text-red-600 hover:text-red-700" title="Delete" style="padding: 0.25rem; border-radius: 0.25rem;">
                            <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                        </button>
                    </div>
                </div>
                ${hasChildren ? renderNodes(node.children, depth + 1, node.id) : ''}
            </div>
        `;
    }).join('');
}

/**
 * Add root node
 */
function addRootNode() {
    const state = window.structureBuilderState;
    const newNode = {
        id: state.nextNodeId++,
        name: 'New Category',
        children: []
    };
    
    state.structure.push(newNode);
    renderStructureTree();
    selectNode(newNode.id);
}

/**
 * Add child node
 */
function addChildNode(parentId) {
    const state = window.structureBuilderState;
    const parent = findNode(state.structure, parentId);
    
    if (!parent) return;
    
    if (!parent.children) {
        parent.children = [];
    }
    
    const newNode = {
        id: state.nextNodeId++,
        name: 'New Subcategory',
        children: []
    };
    
    parent.children.push(newNode);
    renderStructureTree();
    selectNode(newNode.id);
}

/**
 * Find node by ID (recursive)
 */
function findNode(nodes, nodeId) {
    for (const node of nodes) {
        if (node.id === nodeId) return node;
        if (node.children && node.children.length > 0) {
            const found = findNode(node.children, nodeId);
            if (found) return found;
        }
    }
    return null;
}

/**
 * Select node for editing
 */
function selectNode(nodeId) {
    const state = window.structureBuilderState;
    state.selectedNodeId = nodeId;
    
    renderStructureTree();
    renderEditPanel(nodeId);
}

/**
 * Render edit panel for selected node
 */
function renderEditPanel(nodeId) {
    const panel = document.getElementById('edit-panel');
    if (!panel) return;
    
    const state = window.structureBuilderState;
    const node = findNode(state.structure, nodeId);
    
    if (!node) {
        panel.innerHTML = '<div class="text-sm text-gray-500 text-center py-8">Node not found</div>';
        return;
    }
    
    // Get node path
    const path = getNodePath(state.structure, nodeId);
    
    panel.innerHTML = `
        <div class="space-y-4">
            <div>
                <label class="block text-xs font-medium text-gray-700 mb-1">Category Name</label>
                <input type="text" id="node-name-input" value="${escapeHtml(node.name)}" 
                       class="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 text-sm"
                       placeholder="e.g., Basement, Toilets, Kitchen"
                       onkeypress="if(event.key === 'Enter') updateNodeName(${nodeId})">
            </div>
            
            <div class="p-3 bg-blue-50 rounded-md">
                <div class="text-xs font-medium text-blue-900 mb-1">Full Path:</div>
                <div class="text-sm text-blue-700">${path.join(' > ')}</div>
            </div>
            
            <div class="pt-3 border-t border-gray-200">
                <button onclick="updateNodeName(${nodeId})" class="w-full px-3 py-2 bg-primary-600 text-white text-sm font-medium rounded-md hover:bg-primary-700">
                    Update Name
                </button>
            </div>
        </div>
    `;
    
    // Focus input
    setTimeout(() => {
        const input = document.getElementById('node-name-input');
        if (input) {
            input.focus();
            input.select();
        }
    }, 100);
}

/**
 * Get full path to node
 */
function getNodePath(nodes, nodeId, currentPath = []) {
    for (const node of nodes) {
        const newPath = [...currentPath, node.name];
        if (node.id === nodeId) {
            return newPath;
        }
        if (node.children && node.children.length > 0) {
            const found = getNodePath(node.children, nodeId, newPath);
            if (found) return found;
        }
    }
    return null;
}

/**
 * Update node name
 */
function updateNodeName(nodeId) {
    const input = document.getElementById('node-name-input');
    if (!input) return;
    
    const newName = input.value.trim();
    if (!newName) {
        showError('Category name cannot be empty');
        return;
    }
    
    const state = window.structureBuilderState;
    const node = findNode(state.structure, nodeId);
    
    if (!node) return;
    
    node.name = newName;
    renderStructureTree();
    renderEditPanel(nodeId);
    showSuccess('Category name updated');
}

/**
 * Delete node
 */
function deleteNode(nodeId) {
    const state = window.structureBuilderState;
    
    // Find and remove node
    const removed = removeNode(state.structure, nodeId);
    
    if (removed) {
        if (state.selectedNodeId === nodeId) {
            state.selectedNodeId = null;
            document.getElementById('edit-panel').innerHTML = '<div class="text-sm text-gray-500 text-center py-8">Select a node to edit</div>';
        }
        renderStructureTree();
        showSuccess('Category deleted');
    }
}

/**
 * Remove node from tree (recursive)
 */
function removeNode(nodes, nodeId) {
    for (let i = 0; i < nodes.length; i++) {
        if (nodes[i].id === nodeId) {
            nodes.splice(i, 1);
            return true;
        }
        if (nodes[i].children && nodes[i].children.length > 0) {
            if (removeNode(nodes[i].children, nodeId)) {
                return true;
            }
        }
    }
    return false;
}

/**
 * Move a node to a new position within the same parent
 */
function moveNode(draggedNodeId, targetNodeId, parentId) {
    const state = window.structureBuilderState;
    
    // Find the parent array
    let parentArray;
    if (parentId === null) {
        parentArray = state.structure;
    } else {
        const parent = findNode(state.structure, parentId);
        if (!parent || !parent.children) return false;
        parentArray = parent.children;
    }
    
    // Find indices
    const draggedIndex = parentArray.findIndex(n => n.id === draggedNodeId);
    const targetIndex = parentArray.findIndex(n => n.id === targetNodeId);
    
    if (draggedIndex === -1 || targetIndex === -1) return false;
    if (draggedIndex === targetIndex) return false;
    
    // Remove the dragged node
    const [draggedNode] = parentArray.splice(draggedIndex, 1);
    
    // Calculate new insertion position
    // If we removed an item before the target, the target's index has shifted down by 1
    const adjustedTargetIndex = draggedIndex < targetIndex ? targetIndex - 1 : targetIndex;
    
    // Insert the node at the new position
    parentArray.splice(adjustedTargetIndex, 0, draggedNode);
    
    return true;
}

/**
 * Handle drag start
 */
window.handleNodeDragStart = function(event, nodeId) {
    event.stopPropagation();
    window.structureBuilderState.draggedNodeId = nodeId;
    event.dataTransfer.effectAllowed = 'move';
    event.dataTransfer.setData('text/plain', nodeId);
    
    // Add visual feedback
    setTimeout(() => renderStructureTree(), 0);
};

/**
 * Handle drag over
 */
window.handleNodeDragOver = function(event, nodeId) {
    event.preventDefault();
    event.stopPropagation();
    
    const draggedNodeId = window.structureBuilderState.draggedNodeId;
    if (!draggedNodeId || draggedNodeId === nodeId) {
        return;
    }
    
    // Only allow dropping on siblings (same parent)
    const draggedElement = document.querySelector(`[data-node-id="${draggedNodeId}"]`);
    const targetElement = document.querySelector(`[data-node-id="${nodeId}"]`);
    
    if (draggedElement && targetElement) {
        const draggedParentId = draggedElement.dataset.parentId || null;
        const targetParentId = targetElement.dataset.parentId || null;
        
        // Convert empty strings to null for comparison
        const draggedParent = draggedParentId === '' ? null : draggedParentId;
        const targetParent = targetParentId === '' ? null : targetParentId;
        
        if (draggedParent === targetParent) {
            event.dataTransfer.dropEffect = 'move';
            if (window.structureBuilderState.dragOverNodeId !== nodeId) {
                window.structureBuilderState.dragOverNodeId = nodeId;
                renderStructureTree();
            }
        } else {
            event.dataTransfer.dropEffect = 'none';
        }
    }
};

/**
 * Handle drop
 */
window.handleNodeDrop = function(event, targetNodeId, parentId) {
    event.preventDefault();
    event.stopPropagation();
    
    const draggedNodeId = window.structureBuilderState.draggedNodeId;
    if (!draggedNodeId || draggedNodeId === targetNodeId) {
        // Reset drag state
        window.structureBuilderState.draggedNodeId = null;
        window.structureBuilderState.dragOverNodeId = null;
        renderStructureTree();
        return;
    }
    
    // Reset drag state first
    window.structureBuilderState.draggedNodeId = null;
    window.structureBuilderState.dragOverNodeId = null;
    
    // Move the node
    if (moveNode(draggedNodeId, targetNodeId, parentId)) {
        renderStructureTree();
        // Keep the dragged node selected
        if (window.structureBuilderState.selectedNodeId === draggedNodeId) {
            renderEditPanel(draggedNodeId);
        }
    } else {
        // If move failed, just re-render to clean up visual state
        renderStructureTree();
    }
};

/**
 * Handle drag end
 */
window.handleNodeDragEnd = function(event) {
    // Always clean up drag state when drag ends
    setTimeout(() => {
        window.structureBuilderState.draggedNodeId = null;
        window.structureBuilderState.dragOverNodeId = null;
        renderStructureTree();
    }, 0);
};

/**
 * Handle drag leave
 */
window.handleNodeDragLeave = function(event) {
    // Only clear if we're leaving the structure tree container entirely
    const relatedTarget = event.relatedTarget;
    if (!relatedTarget || !relatedTarget.closest('.structure-node')) {
        window.structureBuilderState.dragOverNodeId = null;
        renderStructureTree();
    }
};

/**
 * Save structure
 * 
 * Note: When users create actual documentation entries (reports), each documentation item
 * will need to reference a structure endpoint. This can be done by:
 * 1. Storing the node ID in the documentation entry (e.g., structure_node_id)
 * 2. Or storing the full path (e.g., structure_path: "Basement > Toilets")
 * 
 * The structure itself is stored in the template, and each location can optionally
 * override this structure with their own custom hierarchy.
 */
async function saveStructure(elementId) {
    const state = window.structureBuilderState;
    const elementData = reportTemplatesState.elements.find(el => el.id === elementId);
    
    if (!elementData) return;
    
    // Validate structure has at least one node
    if (state.structure.length === 0) {
        showError('Structure is empty. Add at least one category or cancel.');
        return;
    }
    
    // Save to element config
    if (!elementData.config) {
        elementData.config = {};
    }
    elementData.config.structure = state.structure;
    
    // Update display
    updateElementDisplay(elementId, elementData);
    
    closeStructureBuilder();
    
    // Auto-save template to persist structure to database
    showSuccess('Saving structure to template...');
    try {
        await saveTemplate();
        showSuccess('Structure saved and template updated');
    } catch (error) {
        console.error('Failed to save template after structure update:', error);
        showError('Structure saved locally but failed to save template. Please click Save Template manually.');
    }
}

/**
 * Close structure builder
 */
function closeStructureBuilder() {
    const modal = document.getElementById('structure-builder-modal');
    if (modal) {
        modal.remove();
    }
    window.structureBuilderState = null;
}

/**
 * Open structure builder for the data page documentation element
 */
function openStructureBuilderForDataPage() {
    // Find the documentation element (evaluation-input) in the data-collection zone
    const docElement = reportTemplatesState.elements.find(el => 
        el.page === 'data' && (el.zone === 'data-collection' || el.zone === 'data-collection-top') && el.elementType === 'evaluation-input'
    );
    
    if (!docElement) {
        showError('Please add an Evaluation element to the Documentation section first');
        return;
    }
    
    // Open structure builder with custom callback to update preview
    const originalSave = window.saveStructure;
    window.saveStructure = function(elementId) {
        originalSave(elementId);
        updateStructurePreview();
    };
    
    openStructureBuilder(docElement.id, docElement);
}

/**
 * Update structure preview in designer view
 */
function updateStructurePreview() {
    const previewDiv = document.getElementById('structure-preview');
    const buttonText = document.getElementById('structure-button-text');
    
    if (!previewDiv || !buttonText) return;
    
    // Check if template uses dynamic layout - hide structure section
    const structureContainer = previewDiv.closest('.border-dashed');
    if (reportTemplatesState.documentationLayout === 'dynamic') {
        if (structureContainer) {
            structureContainer.style.display = 'none';
        }
        return;
    } else {
        if (structureContainer) {
            structureContainer.style.display = 'block';
        }
    }
    
    // Find the documentation element
    const docElement = reportTemplatesState.elements.find(el => 
        el.page === 'data' && el.zone === 'data-collection' && el.elementType === 'evaluation-input'
    );
    
    if (!docElement || !docElement.config || !docElement.config.structure || docElement.config.structure.length === 0) {
        previewDiv.innerHTML = 'Optional: Define hierarchical categories to organize documentation';
        buttonText.textContent = 'Add Structure';
        return;
    }
    
    // Count nodes
    const countNodes = (nodes) => {
        let count = nodes.length;
        nodes.forEach(node => {
            if (node.children && node.children.length > 0) {
                count += countNodes(node.children);
            }
        });
        return count;
    };
    
    const totalNodes = countNodes(docElement.config.structure);
    const rootCount = docElement.config.structure.length;
    
    // Generate preview paths (first 3 examples)
    const generatePaths = (nodes, parentPath = []) => {
        let paths = [];
        for (const node of nodes) {
            const currentPath = [...parentPath, node.name];
            paths.push(currentPath.join(' > '));
            if (node.children && node.children.length > 0) {
                paths = paths.concat(generatePaths(node.children, currentPath));
            }
        }
        return paths;
    };
    
    const allPaths = generatePaths(docElement.config.structure);
    const examplePaths = allPaths.slice(0, 3);
    
    previewDiv.innerHTML = `
        <div class="space-y-2">
            <div class="text-xs font-medium text-gray-700">
                Structure defined: ${rootCount} root ${rootCount === 1 ? 'category' : 'categories'}, ${totalNodes} total ${totalNodes === 1 ? 'node' : 'nodes'}
            </div>
            ${examplePaths.length > 0 ? `
                <div class="text-xs text-gray-600">
                    <div class="font-medium mb-1">Example paths:</div>
                    ${examplePaths.map(path => `<div class="pl-2">• ${path}</div>`).join('')}
                    ${allPaths.length > 3 ? `<div class="pl-2 text-gray-400">...and ${allPaths.length - 3} more</div>` : ''}
                </div>
            ` : ''}
        </div>
    `;
    buttonText.textContent = 'Edit Structure';
}

/**
 * Add a new dynamic entry block
 */
window.addDynamicEntryBlock = function() {
    const container = document.getElementById('dynamic-entries-container');
    if (!container) return;
    
    const blockIndex = container.children.length;
    const blockId = `dynamic-block-${Date.now()}-${blockIndex}`;
    
    const blockHtml = `
        <div class="dynamic-entry-block border-2 border-gray-300 rounded-lg p-4 bg-white" data-block-id="${blockId}" data-block-index="${blockIndex}">
            <div class="flex items-center justify-between mb-3">
                <h4 class="text-sm font-semibold text-gray-700">Entry Template ${blockIndex + 1}</h4>
                <button onclick="removeDynamicEntryBlock('${blockId}')" 
                        class="text-red-600 hover:text-red-800 transition-colors">
                    <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                </button>
            </div>
            
            <!-- Single drop zone for documentation input element -->
            <div class="border-2 border-dashed border-gray-300 rounded p-6 min-h-[120px] flex items-center justify-center" 
                 data-drop-zone="dynamic-${blockIndex}" 
                 data-block-index="${blockIndex}">
                <div class="text-center">
                    <svg class="mx-auto h-12 w-12 text-gray-300 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    <p class="text-xs text-gray-400">Drop documentation input element here</p>
                    <p class="text-xs text-gray-300 mt-1">(Evaluation, Image and Text / Image and Text)</p>
                </div>
            </div>
            
            <!-- Multi-entry option -->
            <div class="mt-3 pt-3 border-t border-gray-200">
                <label class="flex items-center cursor-pointer group">
                    <input type="checkbox" 
                           class="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                           onchange="toggleMultiEntry('${blockId}', this.checked)">
                    <span class="ml-2 text-sm text-gray-700 group-hover:text-gray-900">
                        Allow multiple entries
                        <span class="text-xs text-gray-500 block">Users can add multiple instances of this documentation type</span>
                    </span>
                </label>
            </div>
        </div>
    `;
    
    container.insertAdjacentHTML('beforeend', blockHtml);
    
    // Re-initialize drag and drop for new zones
    initializeDragAndDrop();
    
    showSuccess('Entry template added');
};

/**
 * Remove a dynamic entry block
 */
window.removeDynamicEntryBlock = function(blockId) {
    const block = document.querySelector(`[data-block-id="${blockId}"]`);
    if (!block) return;
    
    if (confirm('Remove this entry template?')) {
        // Remove element from state that belongs to this block
        const blockIndex = parseInt(block.dataset.blockIndex);
        reportTemplatesState.elements = reportTemplatesState.elements.filter(el => {
            return el.zone !== `dynamic-${blockIndex}`;
        });
        
        block.remove();
        
        // Renumber remaining blocks
        renumberDynamicBlocks();
        
        showSuccess('Entry template removed');
    }
};

/**
 * Renumber dynamic blocks after deletion
 */
function renumberDynamicBlocks() {
    const container = document.getElementById('dynamic-entries-container');
    if (!container) return;
    
    const blocks = container.querySelectorAll('.dynamic-entry-block');
    blocks.forEach((block, newIndex) => {
        const oldIndex = parseInt(block.dataset.blockIndex);
        block.dataset.blockIndex = newIndex;
        
        // Update title
        const title = block.querySelector('h4');
        if (title) {
            title.textContent = `Entry Template ${newIndex + 1}`;
        }
        
        // Update drop zone
        const zone = block.querySelector('[data-drop-zone]');
        if (zone) {
            const oldZone = zone.dataset.dropZone;
            const newZone = `dynamic-${newIndex}`;
            zone.dataset.dropZone = newZone;
            zone.dataset.blockIndex = newIndex;
            
            // Update elements in state
            reportTemplatesState.elements.forEach(el => {
                if (el.zone === oldZone) {
                    el.zone = newZone;
                }
            });
        }
    });
}

/**
 * Toggle multi-entry setting for a block
 */
window.toggleMultiEntry = function(blockId, enabled) {
    const block = document.querySelector(`[data-block-id="${blockId}"]`);
    if (!block) return;
    
    const blockIndex = parseInt(block.dataset.blockIndex);
    const zoneName = `dynamic-${blockIndex}`;
    
    console.log(`Toggle multi-entry for zone ${zoneName}, enabled=${enabled}`);
    console.log('Elements before update:', reportTemplatesState.elements.filter(el => el.zone === zoneName));
    
    // Update element in this block to have multiEntry flag
    let updatedCount = 0;
    reportTemplatesState.elements.forEach(el => {
        if (el.zone === zoneName) {
            el.multiEntry = enabled;
            updatedCount++;
        }
    });
    
    console.log(`Multi-entry ${enabled ? 'enabled' : 'disabled'} for block ${blockIndex}, updated ${updatedCount} element(s)`);
    console.log('Elements after update:', reportTemplatesState.elements.filter(el => el.zone === zoneName));
};

/**
 * Recreate dynamic blocks when loading a dynamic template
 */
function recreateDynamicBlocks() {
    const container = document.getElementById('dynamic-entries-container');
    if (!container) return;
    
    // Clear existing blocks
    container.innerHTML = '';
    
    // Find all unique dynamic zones from elements
    const dynamicZones = new Set();
    reportTemplatesState.elements.forEach(el => {
        if (el.zone && el.zone.startsWith('dynamic-')) {
            dynamicZones.add(el.zone);
        }
    });
    
    // Sort zones by index
    const sortedZones = Array.from(dynamicZones).sort((a, b) => {
        const indexA = parseInt(a.replace('dynamic-', ''));
        const indexB = parseInt(b.replace('dynamic-', ''));
        return indexA - indexB;
    });
    
    // Create a block for each zone
    sortedZones.forEach(zoneName => {
        const blockIndex = parseInt(zoneName.replace('dynamic-', ''));
        const blockId = `dynamic-block-${blockIndex}`;
        
        // Check if any element in this zone has multiEntry enabled
        const elementInZone = reportTemplatesState.elements.find(el => el.zone === zoneName);
        console.log(`Zone ${zoneName}: elementInZone=`, elementInZone, 'multiEntry=', elementInZone?.multiEntry);
        const multiEntryChecked = elementInZone && elementInZone.multiEntry ? 'checked' : '';
        
        const blockHtml = `
            <div class="dynamic-entry-block border-2 border-gray-300 rounded-lg p-4 bg-white" data-block-id="${blockId}" data-block-index="${blockIndex}">
                <div class="flex items-center justify-between mb-3">
                    <h4 class="text-sm font-semibold text-gray-700">Entry Template ${blockIndex + 1}</h4>
                    <button onclick="removeDynamicEntryBlock('${blockId}')" 
                            class="text-red-600 hover:text-red-800 transition-colors">
                        <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>
                
                <!-- Single drop zone for documentation input element -->
                <div class="border-2 border-dashed border-gray-300 rounded p-6 min-h-[120px] flex items-center justify-center" 
                     data-drop-zone="${zoneName}" 
                     data-block-index="${blockIndex}">
                    <div class="text-center">
                        <svg class="mx-auto h-12 w-12 text-gray-300 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                        <p class="text-xs text-gray-400">Drop documentation input element here</p>
                        <p class="text-xs text-gray-300 mt-1">(Evaluation, Image and Text / Image and Text)</p>
                    </div>
                </div>
                
                <!-- Multi-entry option -->
                <div class="mt-3 pt-3 border-t border-gray-200">
                    <label class="flex items-center cursor-pointer group">
                        <input type="checkbox" 
                               class="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                               onchange="toggleMultiEntry('${blockId}', this.checked)"
                               ${multiEntryChecked}>
                        <span class="ml-2 text-sm text-gray-700 group-hover:text-gray-900">
                            Allow multiple entries
                            <span class="text-xs text-gray-500 block">Users can add multiple instances of this documentation type</span>
                        </span>
                    </label>
                </div>
            </div>
        `;
        
        container.insertAdjacentHTML('beforeend', blockHtml);
    });
    
    // Re-initialize drag and drop for recreated zones
    initializeDragAndDrop();
    
    console.log(`✅ Recreated ${sortedZones.length} dynamic block(s)`);
}

/**
 * Switch between structured and dynamic layouts
 */
function switchDocumentationLayout(layout) {
    const structuredLayout = document.getElementById('structured-layout');
    const dynamicLayout = document.getElementById('dynamic-layout');
    
    if (layout === 'structured') {
        if (structuredLayout) structuredLayout.classList.remove('hidden');
        if (dynamicLayout) dynamicLayout.classList.add('hidden');
    } else if (layout === 'dynamic') {
        if (structuredLayout) structuredLayout.classList.add('hidden');
        if (dynamicLayout) dynamicLayout.classList.remove('hidden');
    }
}

/**
 * Configure evaluation element
 */
function configureEvaluationElement(elementId, elementData) {
    // Get current config or defaults
    const config = elementData.config || {
        scaleMin: 0,
        scaleMax: 10,
        positiveRangeStart: 7,
        positiveRangeEnd: 10,
        allowNotRelevant: false
    };
    
    // Calculate initial range values based on config or defaults
    const scaleMin = config.scaleMin || 0;
    const scaleMax = config.scaleMax || 10;
    const redMax = config.redRangeMax !== undefined ? config.redRangeMax : Math.floor(scaleMin + (scaleMax - scaleMin) / 3);
    const greenMin = config.greenRangeMin !== undefined ? config.greenRangeMin : Math.ceil(scaleMax - (scaleMax - scaleMin) / 3);
    
    // Create modal
    const modal = document.createElement('div');
    modal.className = 'fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50';
    modal.innerHTML = `
        <div class="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div class="mt-3">
                <h3 class="text-lg font-medium text-gray-900 mb-4">Configure Evaluation Scale</h3>
                
                <!-- Intro Text -->
                <div class="mb-4">
                    <label class="block text-sm font-medium text-gray-700 mb-2">Intro Text (Optional)</label>
                    <textarea id="intro-text" rows="2" 
                              class="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                              placeholder="Optional instruction or context for this evaluation...">${elementData.introText || ''}</textarea>
                </div>
                
                <!-- Scale Range -->
                <div class="mb-4">
                    <label class="block text-sm font-medium text-gray-700 mb-2">Scale Range (0-10)</label>
                    <div class="grid grid-cols-2 gap-4">
                        <div>
                            <label class="block text-xs text-gray-500 mb-1">From</label>
                            <input type="number" id="scale-min" min="0" max="10" value="${scaleMin}"
                                   class="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm">
                        </div>
                        <div>
                            <label class="block text-xs text-gray-500 mb-1">To</label>
                            <input type="number" id="scale-max" min="0" max="10" value="${scaleMax}"
                                   class="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm">
                        </div>
                    </div>
                </div>
                
                <!-- Color Ranges -->
                <div class="mb-4">
                    <label class="block text-sm font-medium text-gray-700 mb-3">Color Ranges</label>
                    
                    <!-- Red Range -->
                    <div class="mb-3">
                        <div class="flex items-center justify-between mb-1">
                            <span class="text-xs font-medium text-red-600">🔴 Red (Negative)</span>
                            <span id="red-range-display" class="text-xs text-gray-500"></span>
                        </div>
                        <div>
                            <label class="block text-xs text-gray-500 mb-1">Maximum value (top of red range)</label>
                            <input type="number" id="red-max" min="0" max="10" value="${redMax}"
                                   class="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm">
                        </div>
                    </div>
                    
                    <!-- Yellow Range (Auto-calculated) -->
                    <div class="mb-3">
                        <div class="flex items-center justify-between mb-1">
                            <span class="text-xs font-medium text-yellow-600">🟡 Yellow (Neutral)</span>
                            <span id="yellow-range-display" class="text-xs text-gray-500"></span>
                        </div>
                        <div class="text-xs text-gray-500 italic">Automatically fills the range between Red and Green</div>
                    </div>
                    
                    <!-- Green Range -->
                    <div class="mb-3">
                        <div class="flex items-center justify-between mb-1">
                            <span class="text-xs font-medium text-green-600">🟢 Green (Positive)</span>
                            <span id="green-range-display" class="text-xs text-gray-500"></span>
                        </div>
                        <div>
                            <label class="block text-xs text-gray-500 mb-1">Minimum value (start of green range)</label>
                            <input type="number" id="green-min" min="0" max="10" value="${greenMin}"
                                   class="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm">
                        </div>
                    </div>
                </div>
                
                <!-- Preview -->
                <div class="mb-4 p-3 bg-gray-50 rounded-md">
                    <label class="block text-xs font-medium text-gray-700 mb-2">Preview</label>
                    <div id="scale-preview" class="flex items-center gap-1 flex-wrap"></div>
                </div>
                
                <!-- Not Relevant Option -->
                <div class="mb-4">
                    <label class="flex items-center">
                        <input type="checkbox" id="allow-not-relevant" ${config.allowNotRelevant ? 'checked' : ''}
                               class="rounded border-gray-300 text-primary-600 shadow-sm focus:border-primary-500 focus:ring-primary-500">
                        <span class="ml-2 text-sm text-gray-700">Allow "Not Relevant" selection</span>
                    </label>
                </div>
                
                <!-- Buttons -->
                <div class="flex justify-end space-x-3">
                    <button onclick="closeConfigModal()" 
                            class="px-4 py-2 bg-gray-200 text-gray-800 text-sm font-medium rounded-md hover:bg-gray-300">
                        Cancel
                    </button>
                    <button onclick="saveEvaluationConfig('${elementId}')" 
                            class="px-4 py-2 bg-primary-600 text-white text-sm font-medium rounded-md hover:bg-primary-700">
                        Save Configuration
                    </button>
                </div>
            </div>
        </div>
    `;
    
    document.body.appendChild(modal);
    modal.id = 'config-modal';
    
    // Get input elements
    const scaleMinInput = document.getElementById('scale-min');
    const scaleMaxInput = document.getElementById('scale-max');
    const redMaxInput = document.getElementById('red-max');
    const greenMinInput = document.getElementById('green-min');
    const scalePreview = document.getElementById('scale-preview');
    const redRangeDisplay = document.getElementById('red-range-display');
    const yellowRangeDisplay = document.getElementById('yellow-range-display');
    const greenRangeDisplay = document.getElementById('green-range-display');
    
    // Function to update preview and range displays
    function updatePreview() {
        const min = parseInt(scaleMinInput.value) || 0;
        const max = parseInt(scaleMaxInput.value) || 10;
        const redMax = parseInt(redMaxInput.value);
        const greenMin = parseInt(greenMinInput.value);
        
        // Validate and constrain inputs
        if (min >= max) {
            return;
        }
        
        // Constrain red max to be within scale
        if (redMax < min) redMaxInput.value = min;
        if (redMax > max) redMaxInput.value = max;
        
        // Constrain green min to be within scale
        if (greenMin < min) greenMinInput.value = min;
        if (greenMin > max) greenMinInput.value = max;
        
        const finalRedMax = parseInt(redMaxInput.value);
        const finalGreenMin = parseInt(greenMinInput.value);
        
        // Calculate yellow range
        const yellowMin = finalRedMax + 1;
        const yellowMax = finalGreenMin - 1;
        const hasYellow = yellowMin <= yellowMax;
        
        // Update range displays
        redRangeDisplay.textContent = `${min} - ${finalRedMax}`;
        if (hasYellow) {
            yellowRangeDisplay.textContent = `${yellowMin} - ${yellowMax}`;
            yellowRangeDisplay.classList.remove('text-gray-400');
            yellowRangeDisplay.classList.add('text-gray-500');
        } else {
            yellowRangeDisplay.textContent = '(none)';
            yellowRangeDisplay.classList.remove('text-gray-500');
            yellowRangeDisplay.classList.add('text-gray-400');
        }
        greenRangeDisplay.textContent = `${finalGreenMin} - ${max}`;
        
        // Generate preview
        let previewHtml = '';
        for (let i = min; i <= max; i++) {
            let colorClass, bgColorClass;
            if (i <= finalRedMax) {
                colorClass = 'text-red-600 border-red-600';
                bgColorClass = 'bg-red-50';
            } else if (hasYellow && i >= yellowMin && i <= yellowMax) {
                colorClass = 'text-yellow-600 border-yellow-600';
                bgColorClass = 'bg-yellow-50';
            } else if (i >= finalGreenMin) {
                colorClass = 'text-green-600 border-green-600';
                bgColorClass = 'bg-green-50';
            }
            
            previewHtml += `
                <div class="inline-flex items-center ${bgColorClass} px-2 py-1 rounded border ${colorClass}">
                    <span class="text-xs font-medium">${i}</span>
                </div>
            `;
        }
        
        scalePreview.innerHTML = previewHtml;
    }
    
    // Add event listeners for live updates
    scaleMinInput.addEventListener('input', updatePreview);
    scaleMaxInput.addEventListener('input', updatePreview);
    redMaxInput.addEventListener('input', updatePreview);
    greenMinInput.addEventListener('input', updatePreview);
    
    // Add event listener to reset ranges when scale changes
    scaleMinInput.addEventListener('change', () => {
        const min = parseInt(scaleMinInput.value) || 0;
        const max = parseInt(scaleMaxInput.value) || 10;
        redMaxInput.value = Math.floor(min + (max - min) / 3);
        greenMinInput.value = Math.ceil(max - (max - min) / 3);
        updatePreview();
    });
    
    scaleMaxInput.addEventListener('change', () => {
        const min = parseInt(scaleMinInput.value) || 0;
        const max = parseInt(scaleMaxInput.value) || 10;
        redMaxInput.value = Math.floor(min + (max - min) / 3);
        greenMinInput.value = Math.ceil(max - (max - min) / 3);
        updatePreview();
    });
    
    // Initial preview
    updatePreview();
}

/**
 * Configure text or heading element
 */
function configureTextElement(elementId, elementData, label, inputType) {
    const currentText = (elementData.staticData && elementData.staticData.content) ? elementData.staticData.content : (typeof elementData.staticData === 'string' ? elementData.staticData : '');
    const currentLabel = elementData.introText || '';
    
    const modal = document.createElement('div');
    modal.className = 'fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50';
    modal.innerHTML = `
        <div class="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div class="mt-3">
                <h3 class="text-lg font-medium text-gray-900 mb-4">Configure ${label}</h3>
                
                <div class="mb-4">
                    <label class="block text-sm font-medium text-gray-700 mb-2">Label Text (Optional)</label>
                    <input type="text" id="element-label" 
                           value="${escapeHtml(currentLabel)}"
                           class="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                           placeholder="Optional label or instruction">
                    <p class="mt-1 text-xs text-gray-500">This text will appear above the content</p>
                </div>
                
                <div class="mb-4">
                    <label class="block text-sm font-medium text-gray-700 mb-2">Content</label>
                    ${inputType === 'textarea' ? 
                        `<textarea id="element-text" rows="6" class="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm" placeholder="Enter your content here...">${escapeHtml(currentText)}</textarea>` :
                        `<input type="text" id="element-text" value="${escapeHtml(currentText)}" class="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm" placeholder="Enter heading text...">`
                    }
                </div>
                
                <div class="mb-4 p-3 bg-blue-50 rounded-md">
                    <p class="text-sm text-gray-700">
                        <strong>Preview:</strong><br>
                        <span id="preview-label" class="text-gray-600 text-sm">${escapeHtml(currentLabel) || '(Optional label)'}</span><br>
                        <span id="preview-content" class="${inputType === 'textarea' ? 'text-sm' : 'text-lg font-semibold'}">${escapeHtml(currentText) || '(Content)'}</span>
                    </p>
                </div>
                
                <div class="flex justify-end space-x-3">
                    <button onclick="closeConfigModal()" 
                            class="px-4 py-2 bg-gray-200 text-gray-800 text-sm font-medium rounded-md hover:bg-gray-300">
                        Cancel
                    </button>
                    <button onclick="saveTextConfig('${elementId}')" 
                            class="px-4 py-2 bg-primary-600 text-white text-sm font-medium rounded-md hover:bg-primary-700">
                        Save
                    </button>
                </div>
            </div>
        </div>
    `;
    
    document.body.appendChild(modal);
    modal.id = 'config-modal';
    
    // Add live preview update
    const labelInput = document.getElementById('element-label');
    const textInput = document.getElementById('element-text');
    const previewLabel = document.getElementById('preview-label');
    const previewContent = document.getElementById('preview-content');
    
    labelInput.addEventListener('input', (e) => {
        previewLabel.textContent = e.target.value || '(Optional label)';
    });
    
    textInput.addEventListener('input', (e) => {
        previewContent.textContent = e.target.value || '(Content)';
    });
}

/**
 * Configure creator name element
 */
function configureCreatorNameElement(elementId, elementData) {
    const currentLabel = elementData.introText || '';
    
    const modal = document.createElement('div');
    modal.className = 'fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50';
    modal.innerHTML = `
        <div class="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div class="mt-3">
                <h3 class="text-lg font-medium text-gray-900 mb-4">Configure Creator Name</h3>
                
                <div class="mb-4">
                    <label class="block text-sm font-medium text-gray-700 mb-2">Label Text</label>
                    <input type="text" id="creator-label" 
                           value="${escapeHtml(currentLabel)}"
                           class="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                           placeholder="e.g., Created by:, Prepared by:, Inspector:">
                    <p class="mt-1 text-xs text-gray-500">This text will appear before the creator's name</p>
                </div>
                
                <div class="mb-4 p-3 bg-blue-50 rounded-md">
                    <p class="text-sm text-gray-700">
                        <strong>Preview:</strong><br>
                        <span id="preview-label" class="text-gray-600">${escapeHtml(currentLabel) || '(Label)'}</span><br>
                        <span class="font-semibold">[Creator Name]</span>
                    </p>
                </div>
                
                <div class="flex justify-end space-x-3">
                    <button onclick="closeConfigModal()" 
                            class="px-4 py-2 bg-gray-200 text-gray-800 text-sm font-medium rounded-md hover:bg-gray-300">
                        Cancel
                    </button>
                    <button onclick="saveCreatorNameConfig('${elementId}')" 
                            class="px-4 py-2 bg-primary-600 text-white text-sm font-medium rounded-md hover:bg-primary-700">
                        Save
                    </button>
                </div>
            </div>
        </div>
    `;
    
    document.body.appendChild(modal);
    modal.id = 'config-modal';
    
    // Add live preview update
    const labelInput = document.getElementById('creator-label');
    const previewLabel = document.getElementById('preview-label');
    labelInput.addEventListener('input', (e) => {
        previewLabel.textContent = e.target.value || '(Label)';
    });
}

/**
 * Configure location contacts element
 */
function configureLocationContactsElement(elementId, elementData) {
    const currentLabel = elementData.introText || '';
    
    const modal = document.createElement('div');
    modal.className = 'fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50';
    modal.innerHTML = `
        <div class="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div class="mt-3">
                <h3 class="text-lg font-medium text-gray-900 mb-4">Configure Location Contacts</h3>
                
                <div class="mb-4">
                    <label class="block text-sm font-medium text-gray-700 mb-2">Label Text</label>
                    <input type="text" id="contacts-label" 
                           value="${escapeHtml(currentLabel)}"
                           class="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                           placeholder="e.g., Contacts:, Contact persons:, Location contacts:">
                    <p class="mt-1 text-xs text-gray-500">This text will appear before the contact list</p>
                </div>
                
                <div class="mb-4 p-3 bg-blue-50 rounded-md">
                    <p class="text-sm text-gray-700">
                        <strong>Preview:</strong><br>
                        <span id="preview-contacts-label" class="text-gray-600">${escapeHtml(currentLabel) || '(Label)'}</span><br>
                        <span class="text-sm">• Contact Person 1 - email@example.com<br>• Contact Person 2 - email@example.com</span>
                    </p>
                </div>
                
                <div class="flex justify-end space-x-3">
                    <button onclick="closeConfigModal()" 
                            class="px-4 py-2 bg-gray-200 text-gray-800 text-sm font-medium rounded-md hover:bg-gray-300">
                        Cancel
                    </button>
                    <button onclick="saveLocationContactsConfig('${elementId}')" 
                            class="px-4 py-2 bg-primary-600 text-white text-sm font-medium rounded-md hover:bg-primary-700">
                        Save
                    </button>
                </div>
            </div>
        </div>
    `;
    
    document.body.appendChild(modal);
    modal.id = 'config-modal';
    
    // Add live preview update
    const labelInput = document.getElementById('contacts-label');
    const previewLabel = document.getElementById('preview-contacts-label');
    labelInput.addEventListener('input', (e) => {
        previewLabel.textContent = e.target.value || '(Label)';
    });
}

/**
 * Configure image element
 */
function configureImageElement(elementId, elementData) {
    const currentLabel = elementData.introText || '';
    const currentImage = elementData.staticData?.imageData || '';
    const currentAlignment = elementData.alignment || elementData.staticData?.alignment || 'center';
    
    console.log('Configuring image element:', elementId, 'Alignment:', currentAlignment, 'Full elementData:', elementData);
    
    const modal = document.createElement('div');
    modal.className = 'fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50';
    modal.innerHTML = `
        <div class="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div class="mt-3">
                <h3 class="text-lg font-medium text-gray-900 mb-4">Configure Image</h3>
                
                <div class="mb-4">
                    <label class="block text-sm font-medium text-gray-700 mb-2">Label Text (Optional)</label>
                    <input type="text" id="image-label" 
                           value="${escapeHtml(currentLabel)}"
                           class="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                           placeholder="Optional caption or label">
                    <p class="mt-1 text-xs text-gray-500">This text will appear above the image</p>
                </div>
                
                <div class="mb-4">
                    <label class="block text-sm font-medium text-gray-700 mb-2">Image Alignment</label>
                    <select id="image-alignment" 
                            class="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm px-3 py-2">
                        <option value="left" ${currentAlignment === 'left' ? 'selected' : ''}>Left</option>
                        <option value="center" ${currentAlignment === 'center' ? 'selected' : ''}>Center</option>
                        <option value="right" ${currentAlignment === 'right' ? 'selected' : ''}>Right</option>
                    </select>
                    <p class="mt-1 text-xs text-gray-500">Choose how the image should be aligned in the report</p>
                </div>
                
                <div class="mb-4">
                    <label class="block text-sm font-medium text-gray-700 mb-2">Select Image</label>
                    <input type="file" id="image-file" accept="image/*" 
                           class="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-primary-50 file:text-primary-700 hover:file:bg-primary-100">
                </div>
                
                <div class="mb-4 p-3 bg-blue-50 rounded-md">
                    <p class="text-sm text-gray-700 mb-2"><strong>Preview:</strong></p>
                    <span id="preview-label" class="text-gray-600 text-sm block mb-2">${escapeHtml(currentLabel) || '(Optional label)'}</span>
                    <div id="image-preview-container" style="text-align: ${currentAlignment};" class="${currentImage ? '' : 'hidden'}">
                        <img id="preview-img" src="${currentImage}" class="max-h-40 rounded border border-gray-300" style="display: inline-block;">
                    </div>
                    <div id="no-image-preview" class="${currentImage ? 'hidden' : ''} text-center p-4 bg-gray-100 rounded">
                        <span class="text-gray-400">No image selected</span>
                    </div>
                </div>
                
                <div class="flex justify-end space-x-3">
                    <button onclick="closeConfigModal()" 
                            class="px-4 py-2 bg-gray-200 text-gray-800 text-sm font-medium rounded-md hover:bg-gray-300">
                        Cancel
                    </button>
                    <button onclick="saveImageConfig('${elementId}')" 
                            class="px-4 py-2 bg-primary-600 text-white text-sm font-medium rounded-md hover:bg-primary-700">
                        Save
                    </button>
                </div>
            </div>
        </div>
    `;
    
    document.body.appendChild(modal);
    modal.id = 'config-modal';
    
    // Add live preview updates
    const labelInput = document.getElementById('image-label');
    const previewLabel = document.getElementById('preview-label');
    const alignmentSelect = document.getElementById('image-alignment');
    const previewContainer = document.getElementById('image-preview-container');
    
    labelInput.addEventListener('input', (e) => {
        previewLabel.textContent = e.target.value || '(Optional label)';
    });
    
    alignmentSelect.addEventListener('change', (e) => {
        previewContainer.style.textAlign = e.target.value;
    });
    
    // Add file change handler for preview
    document.getElementById('image-file').addEventListener('change', function(e) {
        const file = e.target.files[0];
        if (file) {
            const reader = new FileReader();
            reader.onload = function(event) {
                const preview = document.getElementById('image-preview-container');
                const noPreview = document.getElementById('no-image-preview');
                const img = document.getElementById('preview-img');
                img.src = event.target.result;
                preview.classList.remove('hidden');
                noPreview.classList.add('hidden');
            };
            reader.readAsDataURL(file);
        }
    });
}

/**
 * Configure input element (text-input, date-input, etc.)
 */
function configureInputElement(elementId, elementData) {
    const elementLabel = formatElementType(elementData.elementType);
    const currentLabel = elementData.introText || '';
    const placeholderText = getElementPlaceholder(elementData.elementType);
    
    const modal = document.createElement('div');
    modal.className = 'fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50';
    modal.innerHTML = `
        <div class="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div class="mt-3">
                <h3 class="text-lg font-medium text-gray-900 mb-4">Configure ${elementLabel}</h3>
                
                <div class="mb-4">
                    <label class="block text-sm font-medium text-gray-700 mb-2">Label Text</label>
                    <input type="text" id="input-label" 
                           value="${escapeHtml(currentLabel)}"
                           class="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                           placeholder="e.g., Location:, Date:, Inspector:">
                    <p class="mt-1 text-xs text-gray-500">This text will appear above the input field</p>
                </div>
                
                <div class="mb-4">
                    <label class="flex items-center">
                        <input type="checkbox" id="input-required" ${elementData.inputRequired ? 'checked' : ''}
                               class="rounded border-gray-300 text-primary-600 shadow-sm focus:border-primary-500 focus:ring-primary-500">
                        <span class="ml-2 text-sm text-gray-700">Required field</span>
                    </label>
                </div>
                
                <div class="mb-4 p-3 bg-blue-50 rounded-md">
                    <p class="text-sm text-gray-700">
                        <strong>Preview:</strong><br>
                        <span id="preview-label" class="text-gray-600 text-sm">${escapeHtml(currentLabel) || '(Label)'}</span><br>
                        <span class="text-gray-500">[${placeholderText}]</span>
                    </p>
                </div>
                
                <div class="flex justify-end space-x-3">
                    <button onclick="closeConfigModal()" 
                            class="px-4 py-2 bg-gray-200 text-gray-800 text-sm font-medium rounded-md hover:bg-gray-300">
                        Cancel
                    </button>
                    <button onclick="saveInputConfig('${elementId}')" 
                            class="px-4 py-2 bg-primary-600 text-white text-sm font-medium rounded-md hover:bg-primary-700">
                        Save
                    </button>
                </div>
            </div>
        </div>
    `;
    
    document.body.appendChild(modal);
    modal.id = 'config-modal';
    
    // Live preview update
    const labelInput = document.getElementById('input-label');
    const previewLabel = document.getElementById('preview-label');
    
    labelInput.addEventListener('input', (e) => {
        previewLabel.textContent = e.target.value || '(Label)';
    });
}

/**
 * Configure filter element (simplified config - only label, values are auto-populated)
 */
function configureFilterElement(elementId, elementData) {
    const elementLabel = formatElementType(elementData.elementType);
    const currentLabel = elementData.introText || '';
    const placeholderText = getElementPlaceholder(elementData.elementType);
    
    const modal = document.createElement('div');
    modal.className = 'fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50';
    modal.innerHTML = `
        <div class="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div class="mt-3">
                <h3 class="text-lg font-medium text-gray-900 mb-4">Configure ${elementLabel}</h3>
                
                <div class="mb-4">
                    <label class="block text-sm font-medium text-gray-700 mb-2">Label Text</label>
                    <input type="text" id="filter-label" 
                           value="${escapeHtml(currentLabel)}"
                           class="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                           placeholder="e.g., Period:, Location:, Asset:">
                    <p class="mt-1 text-xs text-gray-500">This text will appear above the filter value</p>
                </div>
                
                <div class="mb-4 p-3 bg-blue-50 rounded-md">
                    <p class="text-sm text-gray-700">
                        <strong>Preview:</strong><br>
                        <span id="filter-preview-label" class="text-gray-600 text-sm">${escapeHtml(currentLabel) || '(Label)'}</span><br>
                        <span class="text-gray-500">${placeholderText}</span>
                    </p>
                </div>
                
                <div class="mb-4 p-3 bg-yellow-50 rounded-md border border-yellow-200">
                    <p class="text-xs text-gray-700">
                        <svg class="w-4 h-4 inline-block mr-1 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <strong>Note:</strong> Filter values are automatically inherited from the context where the report is generated 
                        (e.g., selected dates, locations, assets from the Time Tracking list).
                    </p>
                </div>
                
                <div class="flex justify-end space-x-3">
                    <button onclick="closeConfigModal()" 
                            class="px-4 py-2 bg-gray-200 text-gray-800 text-sm font-medium rounded-md hover:bg-gray-300">
                        Cancel
                    </button>
                    <button onclick="saveFilterConfig('${elementId}')" 
                            class="px-4 py-2 bg-primary-600 text-white text-sm font-medium rounded-md hover:bg-primary-700">
                        Save
                    </button>
                </div>
            </div>
        </div>
    `;
    
    document.body.appendChild(modal);
    modal.id = 'config-modal';
    
    // Live preview update
    const labelInput = document.getElementById('filter-label');
    const previewLabel = document.getElementById('filter-preview-label');
    
    labelInput.addEventListener('input', (e) => {
        previewLabel.textContent = e.target.value || '(Label)';
    });
}

/**
 * Configure tag input element
 */
async function configureTagInputElement(elementId, elementData) {
    const currentLabel = elementData.introText || '';
    const config = elementData.config || {};
    const selectedCategoryId = config.categoryId || '';
    
    // Load tag structure
    const user = getCurrentUser();
    let tagCategories = [];
    
    try {
        const response = await apiCall(`/tags/structure?account_id=${user.account_id}`, {
            headers: auth.getAuthHeaders()
        });
        
        if (response && response.structure) {
            tagCategories = response.structure;
        }
    } catch (error) {
        console.error('Error loading tag categories:', error);
    }
    
    const modal = document.createElement('div');
    modal.className = 'fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50';
    modal.innerHTML = `
        <div class="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div class="mt-3">
                <h3 class="text-lg font-medium text-gray-900 mb-4">Configure Tag Input</h3>
                
                <div class="mb-4">
                    <label class="block text-sm font-medium text-gray-700 mb-2">Label Text</label>
                    <input type="text" id="tag-input-label" 
                           value="${escapeHtml(currentLabel)}"
                           class="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                           placeholder="e.g., Tags:, Categories:, Labels:">
                    <p class="mt-1 text-xs text-gray-500">This text will appear above the tag selector</p>
                </div>
                
                <div class="mb-4">
                    <label class="block text-sm font-medium text-gray-700 mb-2">Tag Category</label>
                    <select id="tag-category-select"
                            class="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm px-3 py-2">
                        <option value="">Select a category...</option>
                        ${tagCategories.map(cat => `
                            <option value="${cat.id}" data-name="${escapeHtml(cat.name)}" ${cat.id === selectedCategoryId ? 'selected' : ''}>
                                ${escapeHtml(cat.name)} (${cat.children ? cat.children.length : 0} tags)
                            </option>
                        `).join('')}
                    </select>
                    <p class="mt-1 text-xs text-gray-500">Select which tag category to use for this input</p>
                    ${tagCategories.length === 0 ? `
                        <p class="mt-2 text-xs text-amber-600">
                            No tag categories found. Please create tag categories in the Tags tab first.
                        </p>
                    ` : ''}
                </div>
                
                <div class="mb-4">
                    <label class="flex items-center">
                        <input type="checkbox" id="tag-input-required" ${elementData.inputRequired ? 'checked' : ''}
                               class="rounded border-gray-300 text-primary-600 shadow-sm focus:border-primary-500 focus:ring-primary-500">
                        <span class="ml-2 text-sm text-gray-700">Required field</span>
                    </label>
                </div>
                
                <div class="mb-4">
                    <label class="flex items-center">
                        <input type="checkbox" id="tag-input-multiple" ${config.allowMultiple ? 'checked' : ''}
                               class="rounded border-gray-300 text-primary-600 shadow-sm focus:border-primary-500 focus:ring-primary-500">
                        <span class="ml-2 text-sm text-gray-700">Allow multiple selection</span>
                    </label>
                </div>
                
                <div class="mb-4">
                    <label class="flex items-center">
                        <input type="checkbox" id="tag-input-visible" ${config.visibleInReport !== false ? 'checked' : ''}
                               class="rounded border-gray-300 text-primary-600 shadow-sm focus:border-primary-500 focus:ring-primary-500">
                        <span class="ml-2 text-sm text-gray-700">Visible in report</span>
                    </label>
                    <p class="mt-1 ml-6 text-xs text-gray-500">Uncheck to hide this field from the report (useful for tags used only for statistics)</p>
                </div>
                
                <div class="mb-4 p-3 bg-blue-50 rounded-md">
                    <p class="text-sm text-gray-700">
                        <strong>Preview:</strong><br>
                        <span id="preview-tag-label" class="text-gray-600 text-sm">${escapeHtml(currentLabel) || '(Label)'}</span><br>
                        <span id="preview-tag-category" class="text-gray-500 text-sm">[Select tags from: ${config.categoryName || 'Not selected'}]</span>
                    </p>
                </div>
                
                <div class="flex justify-end space-x-3">
                    <button onclick="closeConfigModal()" 
                            class="px-4 py-2 bg-gray-200 text-gray-800 text-sm font-medium rounded-md hover:bg-gray-300">
                        Cancel
                    </button>
                    <button onclick="saveTagInputConfig('${elementId}')" 
                            class="px-4 py-2 bg-primary-600 text-white text-sm font-medium rounded-md hover:bg-primary-700">
                        Save
                    </button>
                </div>
            </div>
        </div>
    `;
    
    document.body.appendChild(modal);
    modal.id = 'config-modal';
    
    // Live preview updates
    const labelInput = document.getElementById('tag-input-label');
    const categorySelect = document.getElementById('tag-category-select');
    const previewLabel = document.getElementById('preview-tag-label');
    const previewCategory = document.getElementById('preview-tag-category');
    
    labelInput.addEventListener('input', (e) => {
        previewLabel.textContent = e.target.value || '(Label)';
    });
    
    categorySelect.addEventListener('change', (e) => {
        const selectedOption = e.target.options[e.target.selectedIndex];
        const categoryName = selectedOption.dataset.name || 'Not selected';
        previewCategory.textContent = `[Select tags from: ${categoryName}]`;
    });
}

/**
 * Configure statistics element
 */
function configureStatisticsElement(elementId, elementData) {
    const currentLabel = elementData.introText || '';
    const config = elementData.config || {};
    const statisticsType = config.statisticsType || 'average-current';
    const displayFormat = config.displayFormat || 'number';
    const icons = config.icons || {};
    
    const modal = document.createElement('div');
    modal.className = 'fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50';
    modal.innerHTML = `
        <div class="relative top-20 mx-auto p-5 border w-full max-w-2xl shadow-lg rounded-md bg-white">
            <div class="mt-3 max-h-[80vh] overflow-y-auto">
                <h3 class="text-lg font-medium text-gray-900 mb-4">Configure Statistics</h3>
                
                <div class="mb-4">
                    <label class="block text-sm font-medium text-gray-700 mb-2">Label Text</label>
                    <input type="text" id="statistics-label" 
                           value="${escapeHtml(currentLabel)}"
                           class="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                           placeholder="e.g., Results Summary, Statistics:">
                    <p class="mt-1 text-xs text-gray-500">This text will appear above the statistics</p>
                </div>
                
                <div class="mb-4">
                    <label class="block text-sm font-medium text-gray-700 mb-2">Statistics Type</label>
                    <select id="statistics-type" 
                            class="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm px-3 py-2">
                        <option value="average-current" ${statisticsType === 'average-current' ? 'selected' : ''}>Average from this report</option>
                        <option value="average-previous" ${statisticsType === 'average-previous' ? 'selected' : ''}>Average from last report (same location)</option>
                        <option value="graph-yearly" ${statisticsType === 'graph-yearly' ? 'selected' : ''}>Graph of averages from last year</option>
                    </select>
                    <p class="mt-1 text-xs text-gray-500">Select the type of statistical data to display</p>
                </div>
                
                <div class="mb-4">
                    <label class="block text-sm font-medium text-gray-700 mb-2">Display Format</label>
                    <select id="display-format" 
                            class="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm px-3 py-2">
                        <option value="number" ${displayFormat === 'number' ? 'selected' : ''}>Number</option>
                        <option value="icon" ${displayFormat === 'icon' ? 'selected' : ''}>Icon</option>
                    </select>
                    <p class="mt-1 text-xs text-gray-500">Choose how to display the average value</p>
                </div>
                
                <div id="value-config-section" class="mb-4 p-4 border border-gray-300 rounded-md">
                    <h4 class="text-sm font-medium text-gray-900 mb-3">Value Configuration (0-10)</h4>
                    <p class="text-xs text-gray-500 mb-3">Configure labels and icons (optional) for each value. Labels will be displayed alongside the value.</p>
                    <div id="value-container" class="space-y-3">
                        ${Array.from({length: 11}, (_, i) => {
                            const iconData = icons[i] || '';
                            const valueLabel = (config.valueLabels && config.valueLabels[i]) || '';
                            return `
                                <div class="border border-gray-200 rounded p-3 bg-gray-50">
                                    <div class="flex items-center space-x-2 mb-2">
                                        <label class="text-sm font-medium text-gray-700 w-16">Value ${i}:</label>
                                        <input type="text" id="value-label-${i}" 
                                               value="${escapeHtml(valueLabel)}"
                                               class="block flex-1 rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 text-sm"
                                               placeholder="Optional label (e.g., Excellent, Poor)">
                                    </div>
                                    <div id="icon-row-${i}" class="${displayFormat === 'icon' ? 'flex' : 'hidden'} items-center space-x-2">
                                        <label class="text-xs text-gray-600 w-16">Icon:</label>
                                        <input type="file" id="icon-file-${i}" accept="image/*" 
                                               class="block flex-1 text-sm text-gray-500 file:mr-4 file:py-1 file:px-3 file:rounded-md file:border-0 file:text-xs file:font-semibold file:bg-primary-50 file:text-primary-700 hover:file:bg-primary-100"
                                               data-icon-value="${i}">
                                        ${iconData ? `<img src="${iconData}" class="h-8 w-8 object-contain border border-gray-300 rounded" id="icon-preview-${i}">` : `<div id="icon-preview-${i}" class="h-8 w-8 bg-gray-100 border border-gray-300 rounded flex items-center justify-center text-xs text-gray-400">-</div>`}
                                    </div>
                                </div>
                            `;
                        }).join('')}
                    </div>
                </div>
                
                <div class="mb-4 p-3 bg-blue-50 rounded-md">
                    <p class="text-sm text-gray-700">
                        <strong>Preview:</strong><br>
                        <span id="preview-label" class="text-gray-600 text-sm">${escapeHtml(currentLabel) || '(Label)'}</span><br>
                        <span id="preview-display" class="text-gray-500 text-sm">[${getStatisticsTypeLabel(statisticsType)} - ${displayFormat === 'icon' ? 'Icon Display' : 'Number Display'}]</span>
                    </p>
                </div>
                
                <div class="flex justify-end space-x-3">
                    <button onclick="closeConfigModal()" 
                            class="px-4 py-2 bg-gray-200 text-gray-800 text-sm font-medium rounded-md hover:bg-gray-300">
                        Cancel
                    </button>
                    <button onclick="saveStatisticsConfig('${elementId}')" 
                            class="px-4 py-2 bg-primary-600 text-white text-sm font-medium rounded-md hover:bg-primary-700">
                        Save
                    </button>
                </div>
            </div>
        </div>
    `;
    
    document.body.appendChild(modal);
    modal.id = 'config-modal';
    
    // Live preview updates
    const labelInput = document.getElementById('statistics-label');
    const typeSelect = document.getElementById('statistics-type');
    const formatSelect = document.getElementById('display-format');
    const previewLabel = document.getElementById('preview-label');
    const previewDisplay = document.getElementById('preview-display');
    
    labelInput.addEventListener('input', (e) => {
        previewLabel.textContent = e.target.value || '(Label)';
    });
    
    typeSelect.addEventListener('change', (e) => {
        updatePreviewDisplay();
    });
    
    formatSelect.addEventListener('change', (e) => {
        const isIcon = e.target.value === 'icon';
        // Show/hide icon upload rows for each value
        for (let i = 0; i <= 10; i++) {
            const iconRow = document.getElementById(`icon-row-${i}`);
            if (iconRow) {
                if (isIcon) {
                    iconRow.classList.remove('hidden');
                    iconRow.classList.add('flex');
                } else {
                    iconRow.classList.add('hidden');
                    iconRow.classList.remove('flex');
                }
            }
        }
        updatePreviewDisplay();
    });
    
    function updatePreviewDisplay() {
        const type = typeSelect.value;
        const format = formatSelect.value;
        previewDisplay.textContent = `[${getStatisticsTypeLabel(type)} - ${format === 'icon' ? 'Icon Display' : 'Number Display'}]`;
    }
    
    // Handle icon file uploads with preview
    for (let i = 0; i <= 10; i++) {
        const fileInput = document.getElementById(`icon-file-${i}`);
        fileInput.addEventListener('change', function(e) {
            const file = e.target.files[0];
            if (file) {
                const reader = new FileReader();
                reader.onload = function(event) {
                    const preview = document.getElementById(`icon-preview-${i}`);
                    preview.outerHTML = `<img src="${event.target.result}" class="h-8 w-8 object-contain border border-gray-300 rounded" id="icon-preview-${i}">`;
                };
                reader.readAsDataURL(file);
            }
        });
    }
}

/**
 * Get human-readable label for statistics type
 */
function getStatisticsTypeLabel(type) {
    const labels = {
        'average-current': 'Average from this report',
        'average-previous': 'Average from last report',
        'graph-yearly': 'Graph of yearly averages'
    };
    return labels[type] || 'Statistical Data';
}

/**
 * Mark template as having unsaved changes and update UI
 */
function markUnsavedChanges() {
    reportTemplatesState.hasUnsavedChanges = true;
    updateSaveButtonState();
}

/**
 * Show unsaved changes dialog with 3 options: save, discard, cancel
 * Returns: 'save', 'discard', or 'cancel'
 */
async function showUnsavedChangesDialog() {
    return new Promise((resolve) => {
        const modal = document.createElement('div');
        modal.className = 'fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50';
        modal.innerHTML = `
            <div class="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
                <div class="mt-3">
                    <div class="flex items-center mb-4">
                        <div class="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-yellow-100">
                            <svg class="h-6 w-6 text-yellow-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                            </svg>
                        </div>
                    </div>
                    <h3 class="text-lg font-medium text-gray-900 text-center mb-2">Unsaved Changes</h3>
                    <p class="text-sm text-gray-500 text-center mb-6">
                        You have unsaved changes to the current template. What would you like to do?
                    </p>
                    <div class="flex flex-col space-y-2">
                        <button id="save-and-continue" 
                                class="w-full px-4 py-2 bg-primary-600 text-white text-sm font-medium rounded-md hover:bg-primary-700">
                            Save & Continue
                        </button>
                        <button id="discard-changes" 
                                class="w-full px-4 py-2 bg-red-600 text-white text-sm font-medium rounded-md hover:bg-red-700">
                            Discard Changes
                        </button>
                        <button id="cancel-action" 
                                class="w-full px-4 py-2 bg-gray-200 text-gray-800 text-sm font-medium rounded-md hover:bg-gray-300">
                            Cancel (Stay Here)
                        </button>
                    </div>
                </div>
            </div>
        `;
        
        document.body.appendChild(modal);
        
        document.getElementById('save-and-continue').onclick = () => {
            document.body.removeChild(modal);
            resolve('save');
        };
        
        document.getElementById('discard-changes').onclick = () => {
            document.body.removeChild(modal);
            resolve('discard');
        };
        
        document.getElementById('cancel-action').onclick = () => {
            document.body.removeChild(modal);
            resolve('cancel');
        };
    });
}

/**
 * Update save button visual state based on unsaved changes
 */
function updateSaveButtonState() {
    const saveBtn = document.getElementById('save-template-btn');
    if (!saveBtn) return;
    
    if (reportTemplatesState.hasUnsavedChanges) {
        saveBtn.classList.add('ring-2', 'ring-yellow-400', 'ring-offset-2');
        saveBtn.innerHTML = `
            <svg class="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            Save Template (Unsaved Changes)
        `;
    } else {
        saveBtn.classList.remove('ring-2', 'ring-yellow-400', 'ring-offset-2');
        saveBtn.innerHTML = `
            <svg class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4"></path>
            </svg>
            Save Template
        `;
    }
}

/**
 * Save evaluation element configuration
 */
function saveEvaluationConfig(elementId) {
    const elementData = reportTemplatesState.elements.find(el => el.id === elementId);
    if (!elementData) return;
    
    // Get values from modal
    const introText = document.getElementById('intro-text').value.trim();
    const scaleMin = parseInt(document.getElementById('scale-min').value);
    const scaleMax = parseInt(document.getElementById('scale-max').value);
    const redMax = parseInt(document.getElementById('red-max').value);
    const greenMin = parseInt(document.getElementById('green-min').value);
    const allowNotRelevant = document.getElementById('allow-not-relevant').checked;
    
    // Validate
    if (scaleMin >= scaleMax) {
        showError('Scale minimum must be less than maximum');
        return;
    }
    if (redMax < scaleMin || redMax > scaleMax) {
        showError('Red maximum must be within the scale range');
        return;
    }
    if (greenMin < scaleMin || greenMin > scaleMax) {
        showError('Green minimum must be within the scale range');
        return;
    }
    if (greenMin <= redMax) {
        showError('Green range must start after red range (or they can be adjacent)');
        return;
    }
    
    // Save intro text and config (preserve existing structure!)
    elementData.introText = introText;
    elementData.config = {
        ...elementData.config,  // Preserve existing config (like structure)
        scaleMin,
        scaleMax,
        redRangeMax: redMax,
        greenRangeMin: greenMin,
        allowNotRelevant
    };
    
    // Update the visual element
    updateElementDisplay(elementId, elementData);
    
    markUnsavedChanges();
    
    closeConfigModal();
    showSuccess('Configuration saved. Click "Save Template" to persist changes.');
}

/**
 * Save text/heading element configuration
 */
function saveTextConfig(elementId) {
    const elementData = reportTemplatesState.elements.find(el => el.id === elementId);
    if (!elementData) return;
    
    const labelText = document.getElementById('element-label').value.trim();
    const text = document.getElementById('element-text').value.trim();
    
    if (!text) {
        showError('Please enter some content');
        return;
    }
    
    // Save label text and static data
    elementData.introText = labelText;
    elementData.staticData = { content: text };
    
    // Update the visual element
    updateElementDisplay(elementId, elementData);
    
    markUnsavedChanges();
    
    closeConfigModal();
    showSuccess('Content saved. Click "Save Template" to persist changes.');
}

/**
 * Save creator name configuration
 */
function saveCreatorNameConfig(elementId) {
    const elementData = reportTemplatesState.elements.find(el => el.id === elementId);
    if (!elementData) return;
    
    const label = document.getElementById('creator-label').value.trim();
    
    // Save label as intro text
    elementData.introText = label;
    
    // Update the visual element
    updateElementDisplay(elementId, elementData);
    
    closeConfigModal();
    showSuccess('Label saved');
}

/**
 * Save location contacts configuration
 */
function saveLocationContactsConfig(elementId) {
    const elementData = reportTemplatesState.elements.find(el => el.id === elementId);
    if (!elementData) return;
    
    const label = document.getElementById('contacts-label').value.trim();
    
    // Save label as intro text
    elementData.introText = label;
    
    // Update the visual element
    updateElementDisplay(elementId, elementData);
    
    closeConfigModal();
    showSuccess('Label saved');
}

/**
 * Save image element configuration
 */
function saveImageConfig(elementId) {
    const elementData = reportTemplatesState.elements.find(el => el.id === elementId);
    if (!elementData) return;
    
    const labelText = document.getElementById('image-label').value.trim();
    const alignment = document.getElementById('image-alignment').value;
    const fileInput = document.getElementById('image-file');
    const file = fileInput.files[0];
    
    // Check if we need to update the image
    if (!file && !elementData.staticData?.imageData) {
        showError('Please select an image');
        return;
    }
    
    // Save label text and alignment
    elementData.introText = labelText;
    elementData.alignment = alignment;
    
    // If no new file selected, just update label and alignment
    if (!file) {
        updateElementDisplay(elementId, elementData);
        markUnsavedChanges();
        closeConfigModal();
        showSuccess('Configuration saved. Click "Save Template" to persist changes.');
        return;
    }
    
    // Read file as base64
    const reader = new FileReader();
    reader.onload = function(event) {
        // Save image data
        elementData.staticData = { imageData: event.target.result };
        
        // Update the visual element
        updateElementDisplay(elementId, elementData);
        
        markUnsavedChanges();
        
        closeConfigModal();
        showSuccess('Image saved. Click \"Save Template\" to persist changes.');
    };
    reader.readAsDataURL(file);
}

/**
 * Save input element configuration
 */
function saveInputConfig(elementId) {
    const elementData = reportTemplatesState.elements.find(el => el.id === elementId);
    if (!elementData) return;
    
    const introText = document.getElementById('input-label').value.trim();
    const required = document.getElementById('input-required').checked;
    
    // Save configuration
    elementData.introText = introText;
    elementData.inputRequired = required;
    
    // Update the visual element
    updateElementDisplay(elementId, elementData);
    
    markUnsavedChanges();
    
    closeConfigModal();
    showSuccess('Configuration saved. Click "Save Template" to persist changes.');
}

/**
 * Save filter element configuration
 */
function saveFilterConfig(elementId) {
    const elementData = reportTemplatesState.elements.find(el => el.id === elementId);
    if (!elementData) return;
    
    const introText = document.getElementById('filter-label').value.trim();
    
    // Save configuration (filter elements don't have required flag, values are auto-populated)
    elementData.introText = introText;
    
    // Update the visual element
    updateElementDisplay(elementId, elementData);
    
    markUnsavedChanges();
    
    closeConfigModal();
    showSuccess('Configuration saved. Click "Save Template" to persist changes.');
}

/**
 * Save tag input element configuration
 */
function saveTagInputConfig(elementId) {
    const elementData = reportTemplatesState.elements.find(el => el.id === elementId);
    if (!elementData) return;
    
    const introText = document.getElementById('tag-input-label').value.trim();
    const required = document.getElementById('tag-input-required').checked;
    const allowMultiple = document.getElementById('tag-input-multiple').checked;
    const visibleInReport = document.getElementById('tag-input-visible').checked;
    const categorySelect = document.getElementById('tag-category-select');
    const categoryId = categorySelect.value;
    const categoryName = categorySelect.options[categorySelect.selectedIndex]?.dataset.name || '';
    
    if (!categoryId) {
        showError('Please select a tag category');
        return;
    }
    
    // Save configuration
    elementData.introText = introText;
    elementData.inputRequired = required;
    
    if (!elementData.config) {
        elementData.config = {};
    }
    elementData.config.categoryId = parseInt(categoryId);
    elementData.config.categoryName = categoryName;
    elementData.config.allowMultiple = allowMultiple;
    elementData.config.visibleInReport = visibleInReport;
    
    console.log('✅ Tag config saved to element state:', {
        elementId,
        visibleInReport,
        config: elementData.config
    });
    
    // Update the visual element
    updateElementDisplay(elementId, elementData);
    
    // Mark as having unsaved changes
    markUnsavedChanges();
    
    closeConfigModal();
    showSuccess('Tag configuration saved. Click "Save Template" to persist changes.');
}

/**
 * Save statistics element configuration
 */
async function saveStatisticsConfig(elementId) {
    const elementData = reportTemplatesState.elements.find(el => el.id === elementId);
    if (!elementData) return;
    
    const introText = document.getElementById('statistics-label').value.trim();
    const statisticsType = document.getElementById('statistics-type').value;
    const displayFormat = document.getElementById('display-format').value;
    
    // Collect value labels (optional for all values)
    const valueLabels = {};
    for (let i = 0; i <= 10; i++) {
        const labelInput = document.getElementById(`value-label-${i}`);
        if (labelInput && labelInput.value.trim()) {
            valueLabels[i] = labelInput.value.trim();
        }
    }
    
    // Collect icon data if icon format is selected
    const icons = {};
    if (displayFormat === 'icon') {
        let hasAnyIcon = false;
        for (let i = 0; i <= 10; i++) {
            const preview = document.getElementById(`icon-preview-${i}`);
            if (preview && preview.tagName === 'IMG') {
                icons[i] = preview.src;
                hasAnyIcon = true;
            }
        }
        
        if (!hasAnyIcon) {
            showError('Please upload at least one icon for icon display format');
            return;
        }
    }
    
    // Save configuration
    elementData.introText = introText;
    elementData.config = {
        statisticsType: statisticsType,
        displayFormat: displayFormat,
        valueLabels: valueLabels,
        icons: icons
    };
    
    // Update the visual element
    updateElementDisplay(elementId, elementData);
    
    markUnsavedChanges();
    
    closeConfigModal();
    showSuccess('Configuration saved. Click "Save Template" to persist changes.');
}

/**
 * Update element display after configuration
 */
function updateElementDisplay(elementId, elementData) {
    const domElement = document.querySelector(`[data-element-id="${elementId}"]`);
    if (domElement) {
        const dropZone = domElement.closest('[data-drop-zone]');
        if (dropZone) {
            dropZone.innerHTML = createTemplateElement(elementData);
        }
    }
}

/**
 * Close configuration modal
 */
function closeConfigModal() {
    const modal = document.getElementById('config-modal');
    if (modal) {
        modal.remove();
    }
}

/**
 * Get placeholder text for a drop zone
 */
function getZonePlaceholder(zoneName) {
    const placeholders = {
        'header-left': 'Header Left',
        'header-right': 'Header Right',
        'main-content': 'Drop element here',
        'headline-left': 'Headline Left',
        'headline-right': 'Headline Right',
        'detail-1': 'Detail 1',
        'detail-2': 'Detail 2',
        'detail-3': 'Detail 3',
        'detail-4': 'Detail 4',
        'footer-left': 'Footer Left',
        'footer-right': 'Footer Right',
        'results-top': 'Results Top',
        'results-upper': 'Results Upper',
        'results-middle': 'Results Middle',
        'results-lower': 'Results Lower',
        'results-bottom': 'Results Bottom',
        'data-collection': 'Documentation'
    };
    return placeholders[zoneName] || 'Drop element here';
}

/**
 * Switch between report pages
 */
function switchReportPage(page) {
    reportTemplatesState.currentPage = page;
    
    // Hide all page canvases
    document.querySelectorAll('.page-canvas').forEach(canvas => {
        canvas.classList.add('hidden');
    });
    
    // Show selected page
    const selectedCanvas = document.getElementById(`canvas-${page}`);
    if (selectedCanvas) {
        selectedCanvas.classList.remove('hidden');
    }
    
    // Update tab styles
    document.querySelectorAll('.page-tab').forEach(tab => {
        const tabPage = tab.dataset.page;
        if (tabPage === page) {
            tab.classList.remove('border-transparent', 'text-gray-500', 'hover:text-gray-700', 'hover:border-gray-300');
            tab.classList.add('border-primary-500', 'text-primary-600');
        } else {
            tab.classList.remove('border-primary-500', 'text-primary-600');
            tab.classList.add('border-transparent', 'text-gray-500', 'hover:text-gray-700', 'hover:border-gray-300');
        }
    });
    
    // Update elements palette for the new page
    updateElementsPalette(page);
    
    // Ensure drop zones have event listeners
    updateDropZones();
    
    // Update structure preview if switching to data page
    if (page === 'data') {
        updateStructurePreview();
    }
}

/**
 * Filter templates based on search
 */
function filterTemplates() {
    const searchQuery = document.getElementById('template-search').value.toLowerCase();
    reportTemplatesState.searchQuery = searchQuery;
    // TODO: Implement filtering
}

/**
 * Create a new report template
 */
export function createReportTemplate() {
    // Show report type selection modal
    showReportTypeModal();
}

/**
 * Show modal to select report type
 */
function showReportTypeModal() {
    const modal = document.createElement('div');
    modal.id = 'report-type-modal';
    modal.className = 'fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50';
    
    modal.innerHTML = `
        <div class="bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4">
            <div class="px-6 py-4 border-b border-gray-200">
                <h3 class="text-lg font-medium text-gray-900">${t('reportTemplates.selectReportType')}</h3>
                <p class="mt-1 text-sm text-gray-500">${t('reportTemplates.chooseTemplateType')}</p>
            </div>
            
            <div class="p-6">
                <div class="grid grid-cols-3 gap-4">
                    <!-- Data Collection -->
                    <button onclick="window.selectReportType('data_collection')" 
                            class="report-type-option group relative flex flex-col items-center p-6 border-2 border-gray-300 rounded-lg hover:border-primary-500 hover:bg-primary-50 transition-all cursor-pointer">
                        <svg class="w-16 h-16 text-primary-600 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
                        </svg>
                        <h4 class="text-lg font-semibold text-gray-900 mb-2">${t('reportTemplates.dataCollection')}</h4>
                        <p class="text-sm text-gray-600 text-center">
                            ${t('reportTemplates.dataCollectionDesc')}
                        </p>
                        <div class="absolute top-3 right-3 w-6 h-6 border-2 border-gray-400 rounded-full group-hover:border-primary-600 group-hover:bg-primary-100 transition-all"></div>
                    </button>
                    
                    <!-- Filter Report -->
                    <button onclick="window.selectReportType('filter')" 
                            class="report-type-option group relative flex flex-col items-center p-6 border-2 border-gray-300 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-all cursor-pointer">
                        <svg class="w-16 h-16 text-blue-600 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
                        </svg>
                        <h4 class="text-lg font-semibold text-gray-900 mb-2">${t('reportTemplates.filterReport')}</h4>
                        <p class="text-sm text-gray-600 text-center">
                            ${t('reportTemplates.filterReportDesc')}
                        </p>
                        <div class="absolute top-3 right-3 w-6 h-6 border-2 border-gray-400 rounded-full group-hover:border-blue-600 group-hover:bg-blue-100 transition-all"></div>
                    </button>
                    
                    <!-- Statistics Report -->
                    <button onclick="window.selectReportType('statistics')" 
                            class="report-type-option group relative flex flex-col items-center p-6 border-2 border-gray-300 rounded-lg hover:border-green-500 hover:bg-green-50 transition-all cursor-pointer">
                        <svg class="w-16 h-16 text-green-600 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                        </svg>
                        <h4 class="text-lg font-semibold text-gray-900 mb-2">${t('reportTemplates.statisticsReport')}</h4>
                        <p class="text-sm text-gray-600 text-center">
                            ${t('reportTemplates.statisticsReportDesc')}
                        </p>
                        <div class="absolute top-3 right-3 w-6 h-6 border-2 border-gray-400 rounded-full group-hover:border-green-600 group-hover:bg-green-100 transition-all"></div>
                    </button>
                </div>
            </div>
            
            <div class="px-6 py-4 bg-gray-50 rounded-b-lg flex justify-end">
                <button onclick="window.closeReportTypeModal()" 
                        class="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50">
                    ${t('reportTemplates.cancel')}
                </button>
            </div>
        </div>
    `;
    
    document.body.appendChild(modal);
    
    // Close on background click
    modal.addEventListener('click', (e) => {
        if (e.target === modal) {
            closeReportTypeModal();
        }
    });
}

/**
 * Select report type and proceed
 */
window.selectReportType = function(reportType) {
    closeReportTypeModal();
    
    // Statistics is not yet implemented
    if (reportType === 'statistics') {
        showError(t('reportTemplates.statisticsComingSoon'));
        setTimeout(() => {
            showReportTypeModal(); // Reopen the modal
        }, 100);
        return;
    }
    
    // Store the selected report type
    reportTemplatesState.reportType = reportType;
    
    // If Data Collection, show documentation layout modal
    if (reportType === 'data_collection') {
        showDocumentationLayoutModal();
    } else {
        // Filter and Statistics reports skip layout selection
        // They only have Frontpage and Results
        reportTemplatesState.documentationLayout = null; // Not applicable
        proceedWithTemplateCreation(reportType);
    }
};

/**
 * Close report type modal
 */
window.closeReportTypeModal = function() {
    const modal = document.getElementById('report-type-modal');
    if (modal) {
        modal.remove();
    }
};

/**
 * Show modal to select documentation layout type (for Data Collection reports)
 */
function showDocumentationLayoutModal() {
    const modal = document.createElement('div');
    modal.id = 'documentation-layout-modal';
    modal.className = 'fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50';
    
    modal.innerHTML = `
        <div class="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4">
            <div class="px-6 py-4 border-b border-gray-200">
                <h3 class="text-lg font-medium text-gray-900">Select Documentation Layout</h3>
                <p class="mt-1 text-sm text-gray-500">Choose how you want to organize documentation in this template</p>
            </div>
            
            <div class="p-6">
                <div class="grid grid-cols-2 gap-4">
                    <!-- Structured Layout -->
                    <button onclick="window.selectDocumentationLayout('structured')" 
                            class="layout-option group relative flex flex-col items-center p-6 border-2 border-gray-300 rounded-lg hover:border-primary-500 hover:bg-primary-50 transition-all cursor-pointer">
                        <svg class="w-16 h-16 text-primary-600 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
                        </svg>
                        <h4 class="text-lg font-semibold text-gray-900 mb-2">Structured</h4>
                        <p class="text-sm text-gray-600 text-center">
                            Pre-defined hierarchical structure with expandable tree navigation. 
                            Best for standardized inspections with fixed areas to document.
                        </p>
                        <div class="absolute top-3 right-3 w-6 h-6 border-2 border-gray-400 rounded-full group-hover:border-primary-600 group-hover:bg-primary-100 transition-all"></div>
                    </button>
                    
                    <!-- Dynamic Layout -->
                    <button onclick="window.selectDocumentationLayout('dynamic')" 
                            class="layout-option group relative flex flex-col items-center p-6 border-2 border-gray-300 rounded-lg hover:border-primary-500 hover:bg-primary-50 transition-all cursor-pointer">
                        <svg class="w-16 h-16 text-green-600 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                        </svg>
                        <h4 class="text-lg font-semibold text-gray-900 mb-2">Dynamic</h4>
                        <p class="text-sm text-gray-600 text-center">
                            Flexible list format where users can add documentation entries as needed. 
                            Best for variable inspections or when structure is not known in advance.
                        </p>
                        <div class="absolute top-3 right-3 w-6 h-6 border-2 border-gray-400 rounded-full group-hover:border-green-600 group-hover:bg-green-100 transition-all"></div>
                    </button>
                </div>
            </div>
            
            <div class="px-6 py-4 bg-gray-50 rounded-b-lg flex justify-end">
                <button onclick="window.closeDocumentationLayoutModal()" 
                        class="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50">
                    Cancel
                </button>
            </div>
        </div>
    `;
    
    document.body.appendChild(modal);
    
    // Close on background click
    modal.addEventListener('click', (e) => {
        if (e.target === modal) {
            closeDocumentationLayoutModal();
        }
    });
}

/**
 * Select documentation layout and proceed with template creation
 */
window.selectDocumentationLayout = function(layoutType) {
    closeDocumentationLayoutModal();
    
    // Store the selected layout type
    reportTemplatesState.documentationLayout = layoutType;
    
    // Proceed with template creation
    proceedWithTemplateCreation(layoutType);
};

/**
 * Close documentation layout modal
 */
window.closeDocumentationLayoutModal = function() {
    const modal = document.getElementById('documentation-layout-modal');
    if (modal) {
        modal.remove();
    }
};

/**
 * Proceed with template creation after layout selection
 */
function proceedWithTemplateCreation(typeOrLayout) {
    // Clear state for new template
    reportTemplatesState.elements = [];
    reportTemplatesState.templateName = '';
    reportTemplatesState.templateDescription = '';
    reportTemplatesState.isReleased = false;  // New templates are always drafts
    
    // Determine report type and layout based on what was passed
    // If it's 'structured' or 'dynamic', it's a Data Collection report
    // Otherwise it's the report type itself (filter, statistics)
    let reportType, layoutType, layoutLabel;
    
    if (typeOrLayout === 'structured' || typeOrLayout === 'dynamic') {
        reportType = 'data_collection';
        layoutType = typeOrLayout;
        layoutLabel = typeOrLayout === 'structured' ? 'Structured' : 'Dynamic';
        reportTemplatesState.documentationLayout = layoutType;
    } else {
        reportType = typeOrLayout;
        layoutType = null; // Filter and Statistics don't have sub-layouts
        layoutLabel = reportType === 'filter' ? 'Filter' : 'Statistics';
        reportTemplatesState.documentationLayout = null;
    }
    
    reportTemplatesState.reportType = reportType;
    
    // Update element palette based on the report type and current page (default to frontpage)
    reportTemplatesState.currentPage = 'frontpage';
    updateElementsPalette('frontpage');
    
    // Clear dynamic blocks container from previous template
    const dynamicContainer = document.getElementById('dynamic-entries-container');
    if (dynamicContainer) {
        dynamicContainer.innerHTML = '';
    }
    
    // Remove empty state from DOM and show canvas immediately
    const emptyState = document.getElementById('empty-state');
    const canvas = document.getElementById('report-canvas');
    
    if (emptyState) {
        emptyState.remove(); // Completely remove from DOM
    }
    if (canvas) {
        canvas.classList.remove('hidden');
        canvas.style.display = 'block';
    }
    
    // Clear all drop zones
    document.querySelectorAll('[data-drop-zone]').forEach(zone => {
        const placeholder = getZonePlaceholder(zone.dataset.dropZone);
        zone.innerHTML = `<p class="text-xs text-gray-300">${placeholder}</p>`;
    });
    
    // Show tabs and configure them based on report type
    const pageTabs = document.getElementById('page-tabs');
    if (pageTabs) {
        pageTabs.classList.remove('hidden');
        
        // Get all tab buttons
        const dataTab = pageTabs.querySelector('[data-page="data"]');
        const conclusionTab = pageTabs.querySelector('[data-page="conclusion"]');
        
        // Show/hide tabs based on report type
        if (reportType === 'filter' || reportType === 'statistics') {
            // Filter and Statistics only have Frontpage and Results
            if (dataTab) dataTab.style.display = 'none';
            if (conclusionTab) conclusionTab.style.display = 'none';
        } else {
            // Data Collection has all tabs
            if (dataTab) dataTab.style.display = '';
            if (conclusionTab) conclusionTab.style.display = '';
        }
    }
    
    // Reset canvas UI for new template (unreleased state)
    document.getElementById('save-template-btn').disabled = false;
    document.getElementById('delete-template-btn').disabled = false;
    document.getElementById('release-template-btn').disabled = false;
    document.getElementById('preview-template-btn').disabled = false;
    document.getElementById('copy-template-btn').classList.remove('hidden');
    document.getElementById('canvas-title').textContent = 'Create New Template';
    document.getElementById('canvas-subtitle').textContent = 'Design your report template by dragging elements';
    
    // Enable drag and drop
    document.querySelectorAll('[data-element-type]').forEach(el => el.draggable = true);
    document.querySelectorAll('[data-drop-zone]').forEach(zone => {
        zone.style.pointerEvents = 'auto';
        zone.style.opacity = '1';
    });
    
    // Add a new template entry to the list with editable name
    const templatesList = document.getElementById('templates-list');
    const tempId = 'temp-' + Date.now();
    
    // Insert new template at the top without replacing existing ones
    const newTemplateHTML = `
        <div class="template-item p-3 hover:bg-gray-50 cursor-pointer border-l-4 border-primary-600 bg-primary-50" data-template-id="${tempId}">
            <input type="text" 
                   id="template-name-input-${tempId}"
                   placeholder="Enter template name..." 
                   class="block w-full border-0 bg-transparent p-0 text-sm font-medium text-gray-900 placeholder-gray-400 focus:ring-0"
                   value="New Template"
                   oninput="onTemplateNameInput('${tempId}')"
                   onblur="saveTemplateName('${tempId}')"
                   onkeypress="if(event.key === 'Enter') saveTemplateName('${tempId}')">
            <p class="mt-1 text-xs text-gray-500">${layoutLabel} • Just created</p>
        </div>
    `;
    
    templatesList.insertAdjacentHTML('afterbegin', newTemplateHTML);
    
    // Switch to appropriate documentation layout (only for Data Collection)
    if (reportType === 'data_collection' && layoutType) {
        switchDocumentationLayout(layoutType);
        
        // If dynamic layout, add first entry block automatically
        if (layoutType === 'dynamic') {
            setTimeout(() => {
                addDynamicEntryBlock();
            }, 100);
        }
    }
    // For Filter and Statistics, no documentation layout to switch
    
    // Focus the input
    setTimeout(() => {
        const input = document.getElementById(`template-name-input-${tempId}`);
        if (input) {
            input.focus();
            input.select();
        }
    }, 100);
    
    reportTemplatesState.selectedTemplateId = tempId;
}

/**
 * Handle template name input - show UI as user types
 */
function onTemplateNameInput(templateId) {
    // Enable the template UI as soon as user starts typing
    if (document.getElementById('report-canvas').classList.contains('hidden')) {
        enableTemplateUI(templateId);
    }
}

/**
 * Save template name
 */
function saveTemplateName(templateId) {
    const input = document.getElementById(`template-name-input-${templateId}`);
    if (!input) return; // Already processed
    
    const name = input.value.trim() || 'Untitled Template';
    
    // Save to state so we can use it when saving the template
    reportTemplatesState.templateName = name;
    
    // Remove event listeners to prevent double firing
    input.onblur = null;
    input.onkeypress = null;
    
    // Convert input to text
    input.outerHTML = `<h4 class="text-sm font-medium text-gray-900">${name}</h4>`;
    showSuccess(`Template "${name}" created`);
}

/**
 * Copy current report template
 */
async function copyReportTemplate() {
    if (!reportTemplatesState.selectedTemplateId) {
        showError('No template selected to copy');
        return;
    }
    
    // Don't copy temporary templates (not saved yet)
    if (reportTemplatesState.selectedTemplateId.startsWith('temp-')) {
        showError('Please save the template before copying');
        return;
    }
    
    try {
        const currentUser = getCurrentUser();
        if (!currentUser || !currentUser.account_id) {
            showError('User not authenticated');
            return;
        }
        
        // Get the current template
        const template = reportTemplatesState.templates.find(t => t._id === reportTemplatesState.selectedTemplateId);
        if (!template) {
            showError('Template not found');
            return;
        }
        
        // Create a copy with " (Copy)" appended to the name
        const copyName = template.name + ' (Copy)';
        
        // Create new template (without elements first)
        const newTemplateData = {
            name: copyName,
            description: template.description || '',
            account_id: currentUser.account_id,
            documentation_layout: template.documentation_layout || 'structured',
            report_type: template.report_type || 'data_collection'
        };
        
        const newTemplate = await createReportTemplateAPI(newTemplateData);
        
        if (newTemplate && newTemplate._id) {
            // Get all elements from the current template using the proper API helper
            const elements = await getTemplateElements(reportTemplatesState.selectedTemplateId);
            console.log(`📋 Copying ${elements.length} elements from template`, elements);
            
            // Copy elements to new template
            if (elements.length > 0) {
                const elementsData = elements.map(el => {
                    const elementData = {
                        template_id: newTemplate._id,
                        position: el.position,
                        page: el.page,
                        zone: el.zone,
                        element_type: el.element_type,
                        input_required: el.input_required || false
                    };
                    
                    // Only include fields with non-null values
                    if (el.intro_text) elementData.intro_text = el.intro_text;
                    if (el.static_data && typeof el.static_data === 'object') elementData.static_data = el.static_data;
                    if (el.config && typeof el.config === 'object') elementData.config = el.config;
                    if (el.input_options && el.input_options.length > 0) elementData.input_options = el.input_options;
                    if (el.alignment) elementData.alignment = el.alignment;
                    
                    return elementData;
                });
                
                console.log('📤 Creating elements in new template:', elementsData);
                await createTemplateElementsBulk(newTemplate._id, elementsData);
                console.log('✅ Elements created successfully');
            }
            
            // Reload templates list
            await loadTemplates();
            
            // Load the new template
            console.log('📥 Loading new template:', newTemplate._id);
            await loadTemplate(newTemplate._id);
            
            showSuccess(`Template copied as "${copyName}"`);
        }
    } catch (error) {
        console.error('Failed to copy template:', error);
        showError('Failed to copy template: ' + (error.message || 'Unknown error'));
    }
}

/**
 * Enable template UI (tabs, buttons)
 */
function enableTemplateUI(templateId) {
    // Hide empty state, show canvas
    const emptyState = document.getElementById('empty-state');
    const canvas = document.getElementById('report-canvas');
    
    if (emptyState) {
        emptyState.classList.add('hidden');
        emptyState.classList.remove('flex');
        emptyState.style.display = 'none';
    }
    if (canvas) {
        canvas.classList.remove('hidden');
        canvas.style.display = 'block';
    }
    
    // Show tabs and enable buttons
    document.getElementById('page-tabs').classList.remove('hidden');
    document.getElementById('preview-template-btn').disabled = false;
    document.getElementById('copy-template-btn').classList.remove('hidden');
    
    // Check if template is released (only for data_collection templates)
    const template = reportTemplatesState.templates.find(t => t._id === templateId);
    const isDataCollection = template?.report_type === 'data_collection' || !template?.report_type;
    const isReleased = isDataCollection && (template?.is_released || false);
    
    // Show/hide release button based on template type
    const releaseBtn = document.getElementById('release-template-btn');
    if (isDataCollection) {
        releaseBtn.style.display = '';  // Show for data collection templates
    } else {
        releaseBtn.style.display = 'none';  // Hide for filter and statistics templates
    }
    
    if (isReleased) {
        // Released templates cannot be edited
        document.getElementById('save-template-btn').disabled = true;
        document.getElementById('delete-template-btn').disabled = true;
        document.getElementById('release-template-btn').disabled = true;
        document.getElementById('canvas-title').textContent = 'View Template (Released)';
        document.getElementById('canvas-subtitle').innerHTML = '<span class="text-green-600">✓ This template is released and ready for reports</span> • Cannot be edited';
        
        // Disable drag and drop
        document.querySelectorAll('[draggable="true"]').forEach(el => el.draggable = false);
        document.querySelectorAll('[data-drop-zone]').forEach(zone => {
            zone.style.pointerEvents = 'none';
            zone.style.opacity = '0.7';
        });
    } else {
        // Draft templates can be edited
        document.getElementById('save-template-btn').disabled = false;
        document.getElementById('delete-template-btn').disabled = false;
        document.getElementById('release-template-btn').disabled = false;
        document.getElementById('canvas-title').textContent = 'Edit Template';
        document.getElementById('canvas-subtitle').textContent = 'Design your report template by dragging elements';
        
        // Enable drag and drop
        document.querySelectorAll('[data-element-type]').forEach(el => el.draggable = true);
        document.querySelectorAll('[data-drop-zone]').forEach(zone => {
            zone.style.pointerEvents = 'auto';
            zone.style.opacity = '1';
        });
    }
    
    reportTemplatesState.selectedTemplateId = templateId;
}

/**
 * Save current template
 */
async function saveTemplate() {
    try {
        const currentUser = getCurrentUser();
        if (!currentUser || !currentUser.account_id) {
            showError('User account not found');
            return;
        }

        // Check if we have elements to save (allow empty for dynamic templates)
        if (reportTemplatesState.elements.length === 0 && reportTemplatesState.documentationLayout !== 'dynamic') {
            showError('Please add at least one element to the template');
            return;
        }

        // If this is a new template (temp-*), create it
        if (reportTemplatesState.selectedTemplateId && reportTemplatesState.selectedTemplateId.startsWith('temp-')) {
            // Get template name from input or use default
            const tempId = reportTemplatesState.selectedTemplateId;
            const nameInput = document.getElementById(`template-name-input-${tempId}`);
            const templateName = nameInput ? nameInput.value.trim() : reportTemplatesState.templateName || 'Untitled Template';
            
            if (!templateName) {
                showError('Please enter a template name');
                return;
            }

            // Create template
            const newTemplate = await createReportTemplateAPI({
                name: templateName,
                description: reportTemplatesState.templateDescription || '',
                account_id: currentUser.account_id,
                documentation_layout: reportTemplatesState.documentationLayout || 'structured',
                report_type: reportTemplatesState.reportType || 'data_collection'
            });

            // Create elements in bulk
            const elementsData = reportTemplatesState.elements.map(el => {
                const elementData = {
                    template_id: newTemplate._id,
                    position: el.position,
                    page: el.page,
                    zone: el.zone,
                    element_type: el.elementType,
                    input_required: el.inputRequired || false,
                    multi_entry: el.multiEntry || false
                };
                
                console.log(`Saving element in zone ${el.zone} with multiEntry:`, el.multiEntry);
                
                // Only include fields with non-null values
                if (el.introText) elementData.intro_text = el.introText;
                if (el.alignment) elementData.alignment = el.alignment;
                if (el.staticData && typeof el.staticData === 'object') elementData.static_data = el.staticData;
                if (el.config && typeof el.config === 'object') elementData.config = el.config;
                if (el.inputOptions && el.inputOptions.length > 0) elementData.input_options = el.inputOptions;
                
                return elementData;
            });

            console.log('Elements data being saved:', elementsData);
            await createTemplateElementsBulk(newTemplate._id, elementsData);

            // Update state with real template ID
            reportTemplatesState.selectedTemplateId = newTemplate._id;
            reportTemplatesState.templateName = templateName;

            // Reload templates list
            await loadTemplates();

            // Select the newly created template
            document.querySelectorAll('.template-item').forEach(item => {
                item.classList.remove('border-l-4', 'border-primary-600', 'bg-primary-50');
            });
            const selectedItem = document.querySelector(`[data-template-id="${newTemplate._id}"]`);
            if (selectedItem) {
                selectedItem.classList.add('border-l-4', 'border-primary-600', 'bg-primary-50');
            }
            
            // Clear unsaved changes flag
            reportTemplatesState.hasUnsavedChanges = false;
            updateSaveButtonState();

            showSuccess(`Template "${templateName}" saved successfully`);
        } else {
            // Update existing template
            const templateId = reportTemplatesState.selectedTemplateId;
            const templateName = reportTemplatesState.templateName;
            
            if (!templateId || templateId.startsWith('temp-')) {
                showError('Invalid template ID');
                return;
            }

            // Check if any reports use this template
            const reportsResponse = await apiCall(`/report-templates/templates/${templateId}/reports-count`, {
                headers: auth.getAuthHeaders()
            });
            const reportsCount = reportsResponse.count || 0;
            
            if (reportsCount > 0) {
                const confirmUpdate = await showConfirmDialog({
                    title: 'Update Template',
                    message: `This template is used by ${reportsCount} existing report${reportsCount > 1 ? 's' : ''}.\n\nChanging the template structure may affect how those reports display.\n\nRecommendation: Consider creating a copy of this template instead.\n\nDo you want to continue with the update?`,
                    confirmText: 'Update Anyway',
                    cancelText: 'Cancel',
                    confirmClass: 'bg-yellow-600 hover:bg-yellow-500',
                    icon: 'warning'
                });
                
                if (!confirmUpdate) {
                    return;
                }
            }

            // Disable save button and show spinner
            const saveBtn = document.getElementById('save-template-btn');
            const originalBtnHtml = saveBtn.innerHTML;
            saveBtn.disabled = true;
            saveBtn.innerHTML = `
                <svg class="animate-spin -ml-1 mr-2 h-4 w-4 inline" fill="none" viewBox="0 0 24 24">
                    <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                    <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Saving...
            `;

            // Prepare new elements data
            const elementsData = reportTemplatesState.elements.map(el => {
                const elementData = {
                    template_id: templateId,
                    position: el.position,
                    page: el.page,
                    zone: el.zone,
                    element_type: el.elementType,
                    input_required: el.inputRequired || false,
                    multi_entry: el.multiEntry || false
                };
                
                console.log(`Updating element in zone ${el.zone} with multiEntry:`, el.multiEntry);
                
                // Only include fields with non-null values
                if (el.introText) elementData.intro_text = el.introText;
                if (el.alignment) elementData.alignment = el.alignment;
                if (el.staticData && typeof el.staticData === 'object') elementData.static_data = el.staticData;
                if (el.config && typeof el.config === 'object') elementData.config = el.config;
                if (el.inputOptions && el.inputOptions.length > 0) elementData.input_options = el.inputOptions;
                
                return elementData;
            });
            
            console.log('Elements data being updated:', elementsData);

            // Update template metadata (including documentation_layout and report_type)
            await updateReportTemplate(templateId, {
                documentation_layout: reportTemplatesState.documentationLayout || 'structured',
                report_type: reportTemplatesState.reportType || 'data_collection'
            });

            // Replace all elements in one API call (delete old + create new)
            await apiCall(`/report-templates/templates/${templateId}/elements`, {
                method: 'PUT',
                headers: auth.getAuthHeaders(),
                body: JSON.stringify(elementsData)
            });

            // Reload templates list to get updated timestamp
            await loadTemplates();

            const successMessage = `Template "${templateName}" updated successfully with ${elementsData.length} element${elementsData.length !== 1 ? 's' : ''}`;
            console.log('✅', successMessage);
            showSuccess(successMessage);
            
            // Clear unsaved changes flag
            reportTemplatesState.hasUnsavedChanges = false;
            updateSaveButtonState();
            
            // Re-enable button (HTML already set by updateSaveButtonState)
            saveBtn.disabled = false;
        }
    } catch (error) {
        console.error('Failed to save template:', error);
        const errorMessage = 'Failed to save template: ' + (error.message || 'Unknown error');
        showError(errorMessage);
        
        // Restore button on error
        const saveBtn = document.getElementById('save-template-btn');
        if (saveBtn) {
            saveBtn.disabled = false;
            saveBtn.innerHTML = `
                <svg class="-ml-1 mr-2 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4" />
                </svg>
                Save
            `;
        }
    }
}

/**
 * Edit template name by clicking on it
 */
async function editTemplateName(templateId, currentName) {
    const templateItem = document.querySelector(`[data-template-id="${templateId}"]`);
    if (!templateItem) return;
    
    const h4 = templateItem.querySelector('h4');
    if (!h4) return;
    
    // Replace h4 with input
    const input = document.createElement('input');
    input.type = 'text';
    input.value = currentName.replace(/&#39;/g, "'");
    input.className = 'block w-full border-0 bg-transparent p-0 text-sm font-medium text-gray-900 placeholder-gray-400 focus:ring-0';
    
    h4.replaceWith(input);
    input.focus();
    input.select();
    
    // Handle save on blur or Enter
    const saveEdit = async () => {
        const newName = input.value.trim() || currentName;
        
        if (newName !== currentName) {
            try {
                // Update template name via API
                await apiCall(`/report-templates/templates/${templateId}`, {
                    method: 'PUT',
                    headers: auth.getAuthHeaders(),
                    body: JSON.stringify({ name: newName })
                });
                
                // Update state if this is the current template
                if (reportTemplatesState.selectedTemplateId === templateId) {
                    reportTemplatesState.templateName = newName;
                }
                
                showSuccess(`Template renamed to "${newName}"`);
            } catch (error) {
                console.error('Failed to rename template:', error);
                showError('Failed to rename template');
                // Reload templates to restore original name
                await loadTemplates();
                return;
            }
        }
        
        // Replace input with h4
        const newH4 = document.createElement('h4');
        newH4.className = 'text-sm font-medium text-gray-900 hover:text-primary-600';
        newH4.textContent = newName;
        newH4.onclick = (e) => {
            e.stopPropagation();
            editTemplateName(templateId, newName);
        };
        input.replaceWith(newH4);
    };
    
    input.onblur = saveEdit;
    input.onkeypress = (e) => {
        if (e.key === 'Enter') {
            saveEdit();
        }
    };
}

/**
 * Delete current template
 */
async function deleteTemplate() {
    const templateId = reportTemplatesState.selectedTemplateId;
    if (!templateId) {
        showError('No template selected');
        return;
    }
    
    // Don't delete temporary templates (not saved yet)
    if (templateId.startsWith('temp-')) {
        showError('Template has not been saved yet');
        return;
    }
    
    const template = reportTemplatesState.templates.find(t => t._id === templateId);
    const templateName = template ? template.name : 'this template';
    
    try {
        // Check if any reports use this template
        const response = await apiCall(`/report-templates/templates/${templateId}/reports-count`, {
            headers: auth.getAuthHeaders()
        });
        const reportsCount = response.count || 0;
        
        let confirmOptions;
        if (reportsCount > 0) {
            confirmOptions = {
                title: 'Archive Template',
                message: `"${templateName}" is used by ${reportsCount} existing report${reportsCount > 1 ? 's' : ''}.\n\nThe template will be archived (not permanently deleted) so existing reports remain intact.\n\nDo you want to continue?`,
                confirmText: 'Archive',
                cancelText: 'Cancel',
                confirmClass: 'bg-yellow-600 hover:bg-yellow-500',
                icon: 'warning'
            };
        } else {
            confirmOptions = {
                title: 'Delete Template',
                message: `Are you sure you want to delete "${templateName}"? This action cannot be undone.`,
                confirmText: 'Delete',
                cancelText: 'Cancel',
                confirmClass: 'bg-red-600 hover:bg-red-500',
                icon: 'danger'
            };
        }
        
        const confirmed = await showConfirmDialog(confirmOptions);
        if (!confirmed) {
            return;
        }
        
        // Delete the template (use force=true to bypass restrictions)
        await deleteReportTemplate(templateId, true);
        
        // Clear canvas
        reportTemplatesState.selectedTemplateId = null;
        reportTemplatesState.templateName = '';
        reportTemplatesState.templateDescription = '';
        reportTemplatesState.elements = [];
        
        // Hide canvas and show empty state
        document.getElementById('report-canvas').classList.add('hidden');
        document.getElementById('empty-state').classList.remove('hidden');
        
        // Disable buttons
        document.getElementById('save-template-btn').disabled = true;
        document.getElementById('delete-template-btn').disabled = true;
        document.getElementById('preview-template-btn').disabled = true;
        document.getElementById('copy-template-btn').classList.add('hidden');
        document.getElementById('page-tabs').classList.add('hidden');
        
        // Reload templates list
        await loadTemplates();
        
        // Show appropriate success message
        if (reportsCount > 0) {
            showSuccess(`Template "${templateName}" archived successfully. Existing reports are unaffected.`);
        } else {
            showSuccess(`Template "${templateName}" deleted successfully`);
        }
    } catch (error) {
        console.error('Failed to delete template:', error);
        showError('Failed to delete template: ' + (error.message || 'Unknown error'));
    }
}

/**
 * Release current template (make immutable)
 */
async function releaseTemplate() {
    const templateId = reportTemplatesState.selectedTemplateId;
    if (!templateId || templateId.startsWith('temp-')) {
        showError('Please save the template before releasing');
        return;
    }
    
    if (reportTemplatesState.elements.length === 0) {
        showError('Cannot release empty template');
        return;
    }
    
    const confirmed = await showConfirmDialog({
        title: 'Release Template',
        message: 'Once released:\n• Cannot be edited or modified\n• Can be used to create reports\n• Must create a copy to make changes\n\nContinue?',
        confirmText: 'Release',
        cancelText: 'Cancel',
        confirmClass: 'bg-green-600 hover:bg-green-500',
        icon: 'info'
    });
    
    if (!confirmed) {
        return;
    }
    
    try {
        await apiCall(`/report-templates/templates/${templateId}`, {
            method: 'PUT',
            headers: auth.getAuthHeaders(),
            body: JSON.stringify({ is_released: true })
        });
        
        // Reload templates list and current template to refresh UI
        await loadTemplates();
        await loadTemplate(templateId);
        
        showSuccess('Template released successfully');
    } catch (error) {
        console.error('Failed to release template:', error);
        showError('Failed to release template: ' + (error.message || 'Unknown error'));
    }
}

/**
 * Preview current template on A4 pages
 */
async function previewTemplate() {
    const templateId = reportTemplatesState.selectedTemplateId;
    const templateName = reportTemplatesState.templateName || 'Untitled Template';
    
    if (!templateId) {
        showError('No template selected');
        return;
    }
    
    if (reportTemplatesState.elements.length === 0) {
        showError('Template is empty. Add some elements first.');
        return;
    }
    
    // Load tag structure for preview rendering
    try {
        const user = getCurrentUser();
        if (user && user.account_id) {
            const response = await apiCall(`/tags/structure?account_id=${user.account_id}`, {
                headers: auth.getAuthHeaders()
            });
            
            if (response && response.structure) {
                window.tagStructureCache = response.structure;
            }
        }
    } catch (error) {
        console.log('Could not load tags for preview:', error);
        window.tagStructureCache = [];
    }
    
    // Create preview modal
    const modal = document.createElement('div');
    modal.className = 'preview-modal';
    modal.innerHTML = `
        <style>
            @media print {
                /* Hide everything except the preview content when printing */
                body > *:not(.preview-modal) {
                    display: none !important;
                }
                
                .preview-modal {
                    position: static !important;
                    background: white !important;
                    padding: 0 !important;
                    overflow: visible !important;
                }
                
                .preview-container {
                    max-width: none !important;
                    margin: 0 !important;
                    box-shadow: none !important;
                    border-radius: 0 !important;
                }
                
                .preview-header {
                    display: none !important;
                }
                
                .preview-body {
                    padding: 0 !important;
                    max-height: none !important;
                    overflow: visible !important;
                }
                
                .preview-pages {
                    display: block !important;
                }
                
                .preview-page {
                    page-break-after: always;
                    margin: 0 !important;
                }
                
                .preview-page:last-child {
                    page-break-after: auto;
                }
                
                .preview-page-label {
                    display: none !important;
                }
            }
        </style>
        <div class="preview-container">
            <div class="preview-header">
                <h2 class="preview-title">Preview: ${escapeHtml(templateName)}</h2>
                <div class="preview-actions">
                    <button onclick="window.print()" class="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50">
                        <svg class="-ml-1 mr-2 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
                        </svg>
                        Print / PDF
                    </button>
                    <button onclick="closePreview()" class="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50">
                        <svg class="-ml-1 mr-2 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                        Close
                    </button>
                </div>
            </div>
            <div class="preview-body">
                <div class="preview-pages" id="preview-pages-container"></div>
            </div>
        </div>
    `;
    
    document.body.appendChild(modal);
    
    // Render pages
    renderPreviewPages();
    
    // Close on background click
    modal.addEventListener('click', (e) => {
        if (e.target === modal) {
            closePreview();
        }
    });
    
    // Close on Escape key
    const escapeHandler = (e) => {
        if (e.key === 'Escape') {
            closePreview();
            document.removeEventListener('keydown', escapeHandler);
        }
    };
    document.addEventListener('keydown', escapeHandler);
}

/**
 * Close preview modal
 */
function closePreview() {
    const modal = document.querySelector('.preview-modal');
    if (modal) {
        modal.remove();
    }
}

/**
 * Render all pages in preview
 */
function renderPreviewPages() {
    const container = document.getElementById('preview-pages-container');
    if (!container) return;
    
    const pages = ['frontpage', 'results', 'data', 'conclusion'];
    const pageLabels = {
        'frontpage': 'Front Page',
        'results': 'Results',
        'data': 'Documentation',
        'conclusion': 'Final Remarks'
    };
    
    container.innerHTML = '';
    
    pages.forEach(page => {
        const pageElements = reportTemplatesState.elements.filter(el => el.page === page);
        
        // Only render page if it has elements
        if (pageElements.length > 0) {
            const pageDiv = document.createElement('div');
            pageDiv.className = 'preview-page';
            pageDiv.innerHTML = `<div class="preview-page-label">${pageLabels[page]}</div>`;
            
            // Render page content based on page type
            const contentHtml = renderPageContent(page, pageElements);
            pageDiv.innerHTML += contentHtml;
            
            container.appendChild(pageDiv);
        }
    });
    
    if (container.children.length === 0) {
        container.innerHTML = '<div class="text-white text-center py-8">No elements to preview</div>';
    }
}

/**
 * Render content for a specific page
 */
function renderPageContent(page, elements) {
    if (page === 'frontpage') {
        return renderFrontPage(elements);
    } else if (page === 'results') {
        return renderResultsPage(elements);
    } else if (page === 'data') {
        return renderDataPage(elements);
    } else if (page === 'conclusion') {
        return renderConclusionPage(elements);
    }
    return '';
}

/**
 * Render front page layout
 */
function renderFrontPage(elements) {
    const zones = {
        'header-left': [],
        'header-right': [],
        'main-content': [],
        'headline-left': [],
        'headline-right': [],
        'detail-1': [],
        'detail-2': [],
        'detail-3': [],
        'detail-4': [],
        'footer-left': [],
        'footer-right': []
    };
    
    // Group elements by zone
    elements.forEach(el => {
        if (zones[el.zone]) {
            zones[el.zone].push(el);
        }
    });
    
    return `
        <div class="preview-header-zones">
            <div class="preview-zone">${renderZoneElements(zones['header-left'])}</div>
            <div class="preview-zone">${renderZoneElements(zones['header-right'])}</div>
        </div>
        
        <div class="preview-zone">
            ${renderZoneElements(zones['main-content'])}
        </div>
        
        <div class="preview-headline-zones">
            <div class="preview-zone">${renderZoneElements(zones['headline-left'])}</div>
            <div class="preview-zone">${renderZoneElements(zones['headline-right'])}</div>
        </div>
        
        <div class="preview-detail-zones">
            <div class="preview-zone">${renderZoneElements(zones['detail-1'])}</div>
            <div class="preview-zone">${renderZoneElements(zones['detail-2'])}</div>
            <div class="preview-zone">${renderZoneElements(zones['detail-3'])}</div>
            <div class="preview-zone">${renderZoneElements(zones['detail-4'])}</div>
        </div>
        
        <div class="preview-footer-zones">
            <div class="preview-zone">${renderZoneElements(zones['footer-left'])}</div>
            <div class="preview-zone">${renderZoneElements(zones['footer-right'])}</div>
        </div>
    `;
}

/**
 * Render results page layout
 */
function renderResultsPage(elements) {
    const zones = {
        'results-top': [],
        'results-upper': [],
        'results-middle': [],
        'results-lower': [],
        'results-bottom': []
    };
    
    elements.forEach(el => {
        if (zones[el.zone]) {
            zones[el.zone].push(el);
        }
    });
    
    return `
        <div class="preview-zone">${renderZoneElements(zones['results-top'])}</div>
        <div class="preview-zone">${renderZoneElements(zones['results-upper'])}</div>
        <div class="preview-zone">${renderZoneElements(zones['results-middle'])}</div>
        <div class="preview-zone">${renderZoneElements(zones['results-lower'])}</div>
        <div class="preview-zone">${renderZoneElements(zones['results-bottom'])}</div>
    `;
}

/**
 * Get all endpoint paths from structure tree (leaf nodes or nodes with no further children)
 * Returns array of full paths like ["Basement > Toilets", "Ground Floor > Kitchen"]
 */
function getAllStructureEndpoints(nodes, parentPath = []) {
    let endpoints = [];
    
    nodes.forEach(node => {
        const currentPath = [...parentPath, node.name];
        
        // If node has no children, it's an endpoint
        if (!node.children || node.children.length === 0) {
            endpoints.push(currentPath.join(' > '));
        } else {
            // If node has children, recurse into them
            endpoints = endpoints.concat(getAllStructureEndpoints(node.children, currentPath));
        }
    });
    
    return endpoints;
}

/**
 * Render data/documentation page layout
 */
function renderDataPage(elements) {
    const zones = {
        'data-collection': []
    };
    
    elements.forEach(el => {
        if (zones[el.zone]) {
            zones[el.zone].push(el);
        }
    });
    
    // Check if there are any documentation elements
    const hasElements = zones['data-collection'].length > 0;
    
    if (!hasElements) {
        return '';
    }
    
    // Check the type of documentation element
    const docElement = zones['data-collection'][0];
    const isEvaluation = docElement.elementType === 'evaluation-input';
    const showEvaluation = isEvaluation; // Only show evaluation if it's an evaluation element
    
    // Get evaluation configuration if it's an evaluation element
    const evalConfig = isEvaluation ? (docElement.config || {}) : {};
    const scaleMin = evalConfig.scaleMin || 0;
    const scaleMax = evalConfig.scaleMax || 10;
    const redMax = evalConfig.redRangeMax !== undefined ? evalConfig.redRangeMax : Math.floor(scaleMin + (scaleMax - scaleMin) / 3);
    const greenMin = evalConfig.greenRangeMin !== undefined ? evalConfig.greenRangeMin : Math.ceil(scaleMax - (scaleMax - scaleMin) / 3);
    const allowNotRelevant = evalConfig.allowNotRelevant || false;
    
    // Calculate yellow range
    const yellowMin = redMax + 1;
    const yellowMax = greenMin - 1;
    const hasYellow = yellowMin <= yellowMax;
    
    // Helper function to determine score color (red/yellow/green)
    const getScoreColor = (score) => {
        if (score <= redMax) {
            return 'red';
        } else if (hasYellow && score >= yellowMin && score <= yellowMax) {
            return 'yellow';
        } else if (score >= greenMin) {
            return 'green';
        }
        return 'gray';
    };
    
    // Pick example scores for each range
    const redScore = scaleMin;
    const yellowScore = hasYellow ? Math.floor((yellowMin + yellowMax) / 2) : null;
    const greenScore = scaleMax;
    
    // Generate example documentation items
    const examples = [];
    
    // Example 1: Green (positive) evaluation
    examples.push({
        number: 1,
        evaluation: showEvaluation ? { score: greenScore, color: getScoreColor(greenScore) } : null,
        text: 'Lorem ipsum dolor sit amet, consectetur adipiscing elit. Sed do eiusmod tempor incididunt ut labore et dolore magna aliqua.',
        images: 2
    });
    
    // Example 2: Red (negative) evaluation
    examples.push({
        number: 2,
        evaluation: showEvaluation ? { score: redScore, color: getScoreColor(redScore) } : null,
        text: 'Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris nisi ut aliquip ex ea commodo consequat.',
        images: 1
    });
    
    // Example 3: Yellow (neutral) evaluation (if yellow range exists)
    if (showEvaluation && hasYellow && yellowScore !== null) {
        examples.push({
            number: 3,
            evaluation: { score: yellowScore, color: getScoreColor(yellowScore) },
            text: 'Duis aute irure dolor in reprehenderit in voluptate velit esse cillum dolore eu fugiat nulla pariatur.',
            images: 1
        });
    }
    
    // Example 4: Not Relevant (if allowed in config)
    if (showEvaluation && allowNotRelevant) {
        examples.push({
            number: hasYellow ? 4 : 3,
            evaluation: { score: '-', color: 'gray' },
            text: 'Excepteur sint occaecat cupidatat non proident, sunt in culpa qui officia deserunt mollit anim id est laborum.',
            images: 1
        });
    }
    
    // Check if structure is defined
    const structure = evalConfig.structure || [];
    const hasStructure = structure.length > 0;
    
    // If structure exists, get all endpoint paths and assign to examples
    let structurePaths = [];
    if (hasStructure) {
        structurePaths = getAllStructureEndpoints(structure);
        
        // Assign paths to examples (cycle through if more examples than paths)
        examples.forEach((example, index) => {
            if (structurePaths.length > 0) {
                example.structurePath = structurePaths[index % structurePaths.length];
            }
        });
    }
    
    const examplesHtml = examples.map(example => `
        ${example.structurePath ? `
            <div style="padding: 1rem 0 0.5rem 0; margin-top: ${example.number === 1 ? '0' : '1rem'};">
                <h3 style="font-size: 1rem; font-weight: 600; color: #1f2937; margin: 0;">
                    ${example.structurePath}
                </h3>
            </div>
        ` : ''}
        <div style="display: grid; grid-template-columns: 2rem 1fr 1fr 2.5rem; gap: 0.75rem; padding: 1rem 0; border-bottom: 1px solid #e5e7eb;">
            <!-- Number Column (minimized) -->
            <div style="display: flex; align-items: flex-start; justify-content: center; padding-top: 0.25rem;">
                <div style="font-size: 1.5rem; font-weight: bold; color: #374151;">${example.number}</div>
            </div>
            
            <!-- Text Column (expanded) -->
            <div style="padding: 0 0.5rem;">
                <p style="font-size: 0.875rem; line-height: 1.4; color: #374151; margin: 0;">
                    ${example.text}
                </p>
            </div>
            
            <!-- Images Column (expanded) -->
            <div style="display: flex; flex-direction: column; gap: 0.5rem; padding: 0 0.5rem;">
                ${Array.from({length: example.images}, (_, i) => `
                    <div style="width: 100%; height: 80px; background: #e5e7eb; border-radius: 0.25rem; display: flex; align-items: center; justify-content: center;">
                        <svg style="width: 32px; height: 32px; color: #9ca3af;" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                    </div>
                `).join('')}
            </div>
            
            <!-- Evaluation Column (minimized) -->
            ${showEvaluation ? `
                <div style="display: flex; align-items: flex-start; justify-content: center; padding-top: 0.25rem;">
                    <div style="font-size: 2rem; font-weight: bold; color: ${example.evaluation.color === 'green' ? '#10b981' : example.evaluation.color === 'yellow' ? '#eab308' : example.evaluation.color === 'red' ? '#ef4444' : '#9ca3af'};">
                        ${example.evaluation.score}
                    </div>
                </div>
            ` : '<div></div>'}
        </div>
    `).join('');
    
    return `
        <div class="preview-zone">
            <div style="margin-bottom: 1rem;">
                <div style="font-weight: 600; color: #374151; margin-bottom: 0.5rem;">Documentation Examples:</div>
                <div style="font-size: 0.875rem; color: #6b7280;">
                    ${hasStructure 
                        ? `Preview showing documentation organized by structure categories (${structurePaths.length} endpoint${structurePaths.length === 1 ? '' : 's'})`
                        : 'Preview showing how documentation items will appear in the final report'
                    }
                </div>
            </div>
            ${examplesHtml}
        </div>
    `;
}

/**
 * Render conclusion page layout
 */
function renderConclusionPage(elements) {
    const zones = {
        'conclusion-content': []
    };
    
    elements.forEach(el => {
        if (zones[el.zone]) {
            zones[el.zone].push(el);
        }
    });
    
    return `
        <div class="preview-zone">${renderZoneElements(zones['conclusion-content'])}</div>
    `;
}

/**
 * Render elements within a zone
 */
function renderZoneElements(elements) {
    if (elements.length === 0) return '';
    
    return elements.map(el => renderElement(el)).join('');
}

/**
 * Render a single element
 */
function renderElement(element) {
    switch (element.elementType) {
        case 'heading':
            return renderHeading(element);
        case 'text-block':
            return renderTextBlock(element);
        case 'image':
            return renderImage(element);
        case 'divider':
            return renderDivider(element);
        case 'creator-name':
            return renderCreatorName(element);
        case 'location-contacts':
            return renderLocationContacts(element);
        case 'text-input':
            return renderTextInput(element);
        case 'date-input':
            return renderDateInput(element);
        case 'location-input':
            return renderLocationInput(element);
        case 'user-input':
            return renderUserInput(element);
        case 'tag-input':
            return renderTagInput(element);
        case 'filter-date-range':
            return renderFilterDateRange(element);
        case 'filter-location':
            return renderFilterLocation(element);
        case 'filter-asset':
            return renderFilterAsset(element);
        case 'filter-account':
            return renderFilterAccount(element);
        case 'filtered-data':
            return renderFilteredData(element);
        case 'evaluation-input':
            return renderEvaluationInput(element);
        case 'image-input':
            return renderImageInput(element);
        case 'statistics-input':
            return renderStatisticsInput(element);
        case 'image-text-input':
            return renderImageTextInput(element);
        default:
            return '';
    }
}

/**
 * Render heading element
 */
function renderHeading(element) {
    const text = element.staticData?.content || 'Heading';
    const size = element.config?.size || 'medium';
    return `<div class="preview-element"><h2 class="preview-heading size-${size}">${escapeHtml(text)}</h2></div>`;
}

/**
 * Render text block element
 */
function renderTextBlock(element) {
    const text = element.staticData?.content || 'Text content';
    return `<div class="preview-element"><div class="preview-text-block">${escapeHtml(text)}</div></div>`;
}

/**
 * Render image element
 */
function renderImage(element) {
    const imageData = element.staticData?.imageData;
    const alignment = element.alignment || 'center';
    const alignmentStyle = `text-align: ${alignment};`;
    
    if (!imageData) {
        return `<div class="preview-element"><div class="preview-image-wrapper" style="background: #e5e7eb; padding: 2rem; text-align: center; color: #9ca3af;">Image</div></div>`;
    }
    return `<div class="preview-element" style="${alignmentStyle}"><div class="preview-image-wrapper" style="display: inline-block;"><img src="${imageData}" alt="Template image" class="preview-image" /></div></div>`;
}

/**
 * Render divider element
 */
function renderDivider(element) {
    const style = element.config?.style || 'solid';
    return `<div class="preview-element"><hr class="preview-divider style-${style}" /></div>`;
}

/**
 * Render creator name element
 */
function renderCreatorName(element) {
    const label = element.introText || '';
    return `
        <div class="preview-element">
            ${label ? `<div class="preview-input-label">${escapeHtml(label)}</div>` : ''}
            <div class="preview-text-block" style="color: #6b7280;">[Creator Name]</div>
        </div>
    `;
}

/**
 * Render location contacts element
 */
function renderLocationContacts(element) {
    const label = element.introText || '';
    return `
        <div class="preview-element">
            ${label ? `<div style="font-size: 0.875rem; font-weight: 600; color: #374151; margin-bottom: 0.125rem;">${escapeHtml(label)}</div>` : ''}
            <div style="font-size: 0.875rem; line-height: 1.2; margin: 0; padding: 0;">• Contact Person 1<br>• Contact Person 2<br>• Contact Person 3</div>
        </div>
    `;
}

/**
 * Render text input placeholder
 */
function renderTextInput(element) {
    const label = element.introText || element.config?.label || 'Text Input';
    
    return `
        <div class="preview-element">
            ${label ? `<div class="preview-input-label">${escapeHtml(label)}</div>` : ''}
            <div class="preview-text-block" style="color: #6b7280;">[Text]</div>
        </div>
    `;
}

/**
 * Render date input placeholder
 */
function renderDateInput(element) {
    const label = element.introText || element.config?.label || 'Date';
    
    return `
        <div class="preview-element">
            ${label ? `<div class="preview-input-label">${escapeHtml(label)}</div>` : ''}
            <div class="preview-text-block" style="color: #6b7280;">[Date]</div>
        </div>
    `;
}

/**
 * Render location input placeholder
 */
function renderLocationInput(element) {
    const label = element.introText || element.config?.label || 'Location';
    const multiple = element.config?.allowMultiple;
    
    return `
        <div class="preview-element">
            ${label ? `<div class="preview-input-label">${escapeHtml(label)}</div>` : ''}
            <div class="preview-text-block" style="color: #6b7280;">[${multiple ? 'Selected Locations' : 'Selected Location'}]</div>
        </div>
    `;
}

/**
 * Render user input placeholder
 */
function renderUserInput(element) {
    const label = element.introText || element.config?.label || 'User';
    const multiple = element.config?.allowMultiple;
    
    return `
        <div class="preview-element">
            ${label ? `<div class="preview-input-label">${escapeHtml(label)}</div>` : ''}
            <div class="preview-text-block" style="color: #6b7280;">[${multiple ? 'Selected Users' : 'Selected User'}]</div>
        </div>
    `;
}

/**
 * Render tag input placeholder
 */
function renderTagInput(element) {
    const config = element.config || {};
    
    // Check if this tag field should be hidden in reports
    if (config.visibleInReport === false) {
        return ''; // Don't render if explicitly set to false
    }
    
    const label = element.introText || config.label || 'Tags';
    const categoryName = config.categoryName || 'Not configured';
    const multiple = config.allowMultiple;
    
    // Try to get the first tag from the category for preview
    const categoryId = config.categoryId;
    let firstTagText = '';
    
    if (categoryId && window.tagStructureCache) {
        const category = window.tagStructureCache.find(cat => cat.id === categoryId);
        if (category && category.children && category.children.length > 0) {
            const firstTag = category.children[0];
            firstTagText = escapeHtml(firstTag.name);
        }
    }
    
    // Fallback to placeholder if no tags available
    if (!firstTagText) {
        firstTagText = `[Tag from ${escapeHtml(categoryName)}]`;
    }
    
    return `
        <div class="preview-element">
            ${label ? `<div class="preview-input-label">${escapeHtml(label)}</div>` : ''}
            <div class="preview-text-block">${firstTagText}</div>
        </div>
    `;
}

/**
 * Render filter date range element
 */
function renderFilterDateRange(element) {
    const label = element.introText || 'Period';
    
    return `
        <div class="preview-element">
            ${label ? `<div class="preview-input-label">${escapeHtml(label)}</div>` : ''}
            <div class="preview-text-block" style="color: #6b7280;">Jan 1, 2026 - Jan 31, 2026</div>
        </div>
    `;
}

/**
 * Render filter location element
 */
function renderFilterLocation(element) {
    const label = element.introText || 'Location';
    
    return `
        <div class="preview-element">
            ${label ? `<div class="preview-input-label">${escapeHtml(label)}</div>` : ''}
            <div class="preview-text-block" style="color: #6b7280;">Building A, Building B</div>
        </div>
    `;
}

/**
 * Render filter asset element
 */
function renderFilterAsset(element) {
    const label = element.introText || 'Asset';
    
    return `
        <div class="preview-element">
            ${label ? `<div class="preview-input-label">${escapeHtml(label)}</div>` : ''}
            <div class="preview-text-block" style="color: #6b7280;">Asset #101, Asset #102</div>
        </div>
    `;
}

/**
 * Render filter account element
 */
function renderFilterAccount(element) {
    const label = element.introText || 'Account';
    
    return `
        <div class="preview-element">
            ${label ? `<div class="preview-input-label">${escapeHtml(label)}</div>` : ''}
            <div class="preview-text-block" style="color: #6b7280;">Company Name</div>
        </div>
    `;
}

/**
 * Render filtered data table
 */
function renderFilteredData(element) {
    const label = element.introText;
    
    return `
        <div class="preview-element">
            ${label ? `<div class="preview-input-label" style="margin-bottom: 0.5rem;">${escapeHtml(label)}</div>` : ''}
            <table style="width: 100%; border-collapse: collapse; font-size: 0.875rem;">
                <thead>
                    <tr style="background-color: #f3f4f6;">
                        <th style="border: 1px solid #d1d5db; padding: 0.5rem; text-align: left;">Date</th>
                        <th style="border: 1px solid #d1d5db; padding: 0.5rem; text-align: left;">Location</th>
                        <th style="border: 1px solid #d1d5db; padding: 0.5rem; text-align: left;">User</th>
                        <th style="border: 1px solid #d1d5db; padding: 0.5rem; text-align: right;">Hours</th>
                    </tr>
                </thead>
                <tbody>
                    <tr>
                        <td style="border: 1px solid #d1d5db; padding: 0.5rem;">Jan 15, 2026</td>
                        <td style="border: 1px solid #d1d5db; padding: 0.5rem;">Building A</td>
                        <td style="border: 1px solid #d1d5db; padding: 0.5rem;">John Doe</td>
                        <td style="border: 1px solid #d1d5db; padding: 0.5rem; text-align: right;">8.5</td>
                    </tr>
                    <tr style="background-color: #f9fafb;">
                        <td style="border: 1px solid #d1d5db; padding: 0.5rem;">Jan 16, 2026</td>
                        <td style="border: 1px solid #d1d5db; padding: 0.5rem;">Building B</td>
                        <td style="border: 1px solid #d1d5db; padding: 0.5rem;">Jane Smith</td>
                        <td style="border: 1px solid #d1d5db; padding: 0.5rem; text-align: right;">7.0</td>
                    </tr>
                    <tr>
                        <td style="border: 1px solid #d1d5db; padding: 0.5rem;">Jan 17, 2026</td>
                        <td style="border: 1px solid #d1d5db; padding: 0.5rem;">Building A</td>
                        <td style="border: 1px solid #d1d5db; padding: 0.5rem;">John Doe</td>
                        <td style="border: 1px solid #d1d5db; padding: 0.5rem; text-align: right;">9.0</td>
                    </tr>
                    <tr style="background-color: #f9fafb;">
                        <td style="border: 1px solid #d1d5db; padding: 0.5rem;">Jan 18, 2026</td>
                        <td style="border: 1px solid #d1d5db; padding: 0.5rem;">Building B</td>
                        <td style="border: 1px solid #d1d5db; padding: 0.5rem;">Jane Smith</td>
                        <td style="border: 1px solid #d1d5db; padding: 0.5rem; text-align: right;">6.5</td>
                    </tr>
                </tbody>
            </table>
        </div>
    `;
}

/**
 * Render evaluation input placeholder
 */
function renderEvaluationInput(element) {
    const label = element.config?.label || 'Evaluation';
    const intro = element.introText || '';
    const required = element.inputRequired ? ' required' : '';
    const config = element.config || {};
    const min = config.scaleMin || 0;
    const max = config.scaleMax || 10;
    const redMax = config.redRangeMax !== undefined ? config.redRangeMax : Math.floor(min + (max - min) / 3);
    const greenMin = config.greenRangeMin !== undefined ? config.greenRangeMin : Math.ceil(max - (max - min) / 3);
    
    // Calculate yellow range
    const yellowMin = redMax + 1;
    const yellowMax = greenMin - 1;
    const hasYellow = yellowMin <= yellowMax;
    
    const scales = [];
    for (let i = min; i <= max; i++) {
        let colorClass = '';
        if (i <= redMax) {
            colorClass = ' negative';
        } else if (hasYellow && i >= yellowMin && i <= yellowMax) {
            colorClass = ' neutral';
        } else if (i >= greenMin) {
            colorClass = ' positive';
        }
        scales.push(`<div class="preview-evaluation-item${colorClass}">${i}</div>`);
    }
    
    return `
        <div class="preview-element preview-evaluation${required}">
            <label class="preview-input-label">${escapeHtml(label)}</label>
            ${intro ? `<div class="preview-input-intro">${escapeHtml(intro)}</div>` : ''}
            <div class="preview-evaluation-scale">${scales.join('')}</div>
            ${config.allowNotRelevant ? '<div style="margin-top: 0.5rem; font-size: 0.75rem; color: #6b7280;">☐ Not Relevant</div>' : ''}
        </div>
    `;
}

/**
 * Render image input placeholder
 */
function renderImageInput(element) {
    const label = element.introText || element.config?.label || 'Image';
    
    return `
        <div class="preview-element">
            ${label ? `<div class="preview-input-label">${escapeHtml(label)}</div>` : ''}
            <div style="width: 100%; height: 150px; background: #e5e7eb; border-radius: 0.25rem; display: flex; align-items: center; justify-content: center; color: #9ca3af;">
                [Image]
            </div>
        </div>
    `;
}

/**
 * Render statistics input placeholder
 */
function renderStatisticsInput(element) {
    const label = element.introText || element.config?.label || 'Statistics';
    const statisticsType = element.config?.statisticsType || 'average-current';
    const displayFormat = element.config?.displayFormat || 'number';
    const config = element.config || {};
    const valueLabels = config.valueLabels || {};
    const icons = config.icons || {};
    
    // For average types (current and previous), show in two equal columns: label left, value right (left-aligned)
    if (statisticsType === 'average-current' || statisticsType === 'average-previous') {
        // Use example value of 4
        const exampleValue = 4;
        const valueLabel = valueLabels[exampleValue] || '';
        
        // Calculate date 2 months back for preview (for average-previous only)
        let dateText = '';
        if (statisticsType === 'average-previous') {
            const date = new Date();
            date.setMonth(date.getMonth() - 2);
            const options = { year: 'numeric', month: 'long', day: 'numeric' };
            dateText = date.toLocaleDateString('en-US', options);
        }
        
        // Determine what to display based on format
        let displayContent;
        if (displayFormat === 'icon') {
            // Try to find an icon, or use a placeholder
            if (icons[exampleValue]) {
                displayContent = `<img src="${icons[exampleValue]}" alt="Rating ${exampleValue}" style="width: 48px; height: 48px; object-fit: contain;">`;
            } else {
                // Find first available icon as example, or show placeholder
                const firstIconValue = Object.keys(icons).find(key => icons[key]);
                if (firstIconValue && icons[firstIconValue]) {
                    displayContent = `<img src="${icons[firstIconValue]}" alt="Rating icon" style="width: 48px; height: 48px; object-fit: contain;">`;
                } else {
                    // Placeholder icon
                    displayContent = `<div style="width: 48px; height: 48px; background: #e5e7eb; border-radius: 0.25rem; display: flex; align-items: center; justify-content: center;">
                        <svg style="width: 24px; height: 24px; color: #9ca3af;" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                    </div>`;
                }
            }
        } else {
            // Display as number
            displayContent = `<span style="font-size: 2rem; font-weight: bold; color: #374151;">${exampleValue}</span>`;
        }
        
        return `
            <div class="preview-element">
                ${label ? `<div class="preview-input-label" style="margin-bottom: 0.5rem;">${escapeHtml(label)}</div>` : ''}
                ${dateText ? `<div style="font-size: 0.75rem; color: #9ca3af; margin-bottom: 0.5rem;">${dateText}</div>` : ''}
                <div style="padding: 1rem; background: #f9fafb; border-radius: 0.5rem;">
                    <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 1rem; align-items: start;">
                        <div style="display: flex; align-items: center; height: 48px;">
                        </div>
                        <div style="display: flex; flex-direction: column; align-items: flex-start; gap: 0.5rem;">
                            ${displayContent}
                            ${valueLabel ? `<div style="font-size: 0.875rem; color: #6b7280;">${escapeHtml(valueLabel)}</div>` : ''}
                        </div>
                    </div>
                </div>
            </div>
        `;
    }
    
    // For graph type, show a simple line graph with 5 random points
    if (statisticsType === 'graph-yearly') {
        // Generate 5 random whole number values between 0 and 6
        const dataPoints = [3, 4, 2, 5, 4];
        const maxValue = 6;
        const graphWidth = 600;
        const graphHeight = 150;
        const padding = 30;
        const pointSpacing = (graphWidth - padding * 2) / (dataPoints.length - 1);
        
        // Create SVG path for the line
        const points = dataPoints.map((value, index) => {
            const x = padding + index * pointSpacing;
            const y = graphHeight - padding - ((value / maxValue) * (graphHeight - padding * 2));
            return `${x},${y}`;
        }).join(' ');
        
        // Calculate Y positions for all labels (0-6)
        const yLabels = [];
        for (let i = 6; i >= 0; i--) {
            const yPos = graphHeight - padding - ((i / maxValue) * (graphHeight - padding * 2));
            yLabels.push({ value: i, y: yPos });
        }
        
        return `
            <div class="preview-element">
                ${label ? `<div class="preview-input-label" style="margin-bottom: 0.75rem;">${escapeHtml(label)}</div>` : ''}
                <div style="padding: 1rem; background: #f9fafb; border-radius: 0.5rem;">
                    <svg width="100%" height="${graphHeight}" viewBox="0 0 ${graphWidth} ${graphHeight}" style="background: white; border-radius: 0.25rem;">
                        <!-- Grid lines -->
                        <line x1="${padding}" y1="${padding}" x2="${padding}" y2="${graphHeight - padding}" stroke="#e5e7eb" stroke-width="1"/>
                        <line x1="${padding}" y1="${graphHeight - padding}" x2="${graphWidth - padding}" y2="${graphHeight - padding}" stroke="#e5e7eb" stroke-width="1"/>
                        
                        <!-- Data line -->
                        <polyline points="${points}" fill="none" stroke="#3b82f6" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                        
                        <!-- Data points -->
                        ${dataPoints.map((value, index) => {
                            const x = padding + index * pointSpacing;
                            const y = graphHeight - padding - ((value / maxValue) * (graphHeight - padding * 2));
                            return `<circle cx="${x}" cy="${y}" r="4" fill="#3b82f6"/>`;
                        }).join('')}
                        
                        <!-- Y-axis labels (0-6) -->
                        ${yLabels.map(label => `<text x="${padding - 10}" y="${label.y + 4}" text-anchor="end" font-size="10" fill="#6b7280">${label.value}</text>`).join('')}
                    </svg>
                    <div style="display: flex; justify-content: space-around; margin-top: 0.5rem; font-size: 0.75rem; color: #9ca3af;">
                        <span>Jan</span>
                        <span>Mar</span>
                        <span>May</span>
                        <span>Jul</span>
                        <span>Sep</span>
                    </div>
                </div>
            </div>
        `;
    }
    
    return '';
}

/**
 * Render image-text input placeholder
 */
function renderImageTextInput(element) {
    const label = element.introText || element.config?.label || 'Image with Description';
    
    return `
        <div class="preview-element">
            ${label ? `<div class="preview-input-label">${escapeHtml(label)}</div>` : ''}
            <div style="width: 100%; height: 150px; background: #e5e7eb; border-radius: 0.25rem; display: flex; align-items: center; justify-content: center; color: #9ca3af; margin-bottom: 0.5rem;">
                [Image]
            </div>
            <div class="preview-text-block" style="color: #6b7280;">[Description]</div>
        </div>
    `;
}

// Export for window access
window.createReportTemplate = createReportTemplate;
window.loadTemplate = loadTemplate;
window.copyReportTemplate = copyReportTemplate;
window.onTemplateNameInput = onTemplateNameInput;
window.saveTemplateName = saveTemplateName;
window.editTemplateName = editTemplateName;
window.deleteTemplate = deleteTemplate;
window.releaseTemplate = releaseTemplate;
window.filterTemplates = filterTemplates;
window.saveTemplate = saveTemplate;
window.previewTemplate = previewTemplate;
window.closePreview = closePreview;
window.switchReportPage = switchReportPage;
window.removeTemplateElement = removeTemplateElement;
window.configureElement = configureElement;
window.saveEvaluationConfig = saveEvaluationConfig;
window.saveTextConfig = saveTextConfig;
window.saveCreatorNameConfig = saveCreatorNameConfig;
window.saveLocationContactsConfig = saveLocationContactsConfig;
window.saveImageConfig = saveImageConfig;
window.saveInputConfig = saveInputConfig;
window.saveFilterConfig = saveFilterConfig;
window.saveTagInputConfig = saveTagInputConfig;
window.saveStatisticsConfig = saveStatisticsConfig;
window.closeConfigModal = closeConfigModal;
window.openStructureBuilder = openStructureBuilder;
window.closeStructureBuilder = closeStructureBuilder;
window.openStructureBuilderForDataPage = openStructureBuilderForDataPage;
window.updateStructurePreview = updateStructurePreview;
window.addRootNode = addRootNode;
window.addChildNode = addChildNode;
window.selectNode = selectNode;
window.updateNodeName = updateNodeName;
window.deleteNode = deleteNode;
window.saveStructure = saveStructure;

/**
 * Handle drag start for existing elements
 */
function handleElementDragStart(e) {
    const element = e.currentTarget;
    const elementId = element.dataset.elementId;
    const elementType = element.dataset.elementType;
    
    // Store the element ID so we know we're moving an existing element
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('elementId', elementId);
    e.dataTransfer.setData('elementType', elementType);
    
    // Add visual feedback
    element.classList.add('opacity-50');
}

/**
 * Handle drag end for existing elements
 */
function handleElementDragEnd(e) {
    const element = e.currentTarget;
    element.classList.remove('opacity-50');
}

window.handleElementDragStart = handleElementDragStart;
window.handleElementDragEnd = handleElementDragEnd;

/**
 * Check if there are unsaved changes and handle navigation
 * Returns true if navigation should proceed, false if cancelled
 */
export async function checkUnsavedChanges() {
    if (reportTemplatesState.hasUnsavedChanges) {
        const result = await showUnsavedChangesDialog();
        
        if (result === 'cancel') {
            return false; // Cancel navigation
        } else if (result === 'save') {
            await saveTemplate();
        }
        // If 'discard', continue with navigation
        
        // Clear unsaved changes flag
        reportTemplatesState.hasUnsavedChanges = false;
        updateSaveButtonState();
    }
    return true; // Proceed with navigation
}

// Make checkUnsavedChanges available globally
window.checkTemplateUnsavedChanges = checkUnsavedChanges;

export default {
    showReportTemplates,
    createReportTemplate,
    checkUnsavedChanges
};
